/**
 * Merchant module translations - English
 * Includes: brand, merchant, policy, workbench, negotiation, intention, plan, task, contract, businessPlan, placement
 */
export default {
  merchant: {
    // ==================== Brand Management ====================
    brand: {
      title: 'Brand Management',
      brandName: 'Brand Name',
      brandCode: 'Brand Code',
      brandLevel: 'Brand Level',
      brandType: 'Brand Type',
      brandDescription: 'Brand Description',
      contactPerson: 'Contact Person',
      contactPhone: 'Contact Phone',
      contactEmail: 'Contact Email',
      foundingYear: 'Founding Year',
      headquarters: 'Headquarters',
      website: 'Website',
      cooperationCases: 'Cooperation Cases',
      cooperationCasesPlaceholder: 'Enter cooperation cases (e.g. XX Plaza, XX Mall)',
      isPreferred: 'Preferred',
      isAlternative: 'Alternative',
      preferredBrand: 'Preferred Brand',
      alternativeBrand: 'Alternative Brand',
      approvalStatus: 'Approval Status',
      cooperationStatus: 'Cooperation Status',
      keyword: 'Keyword',
      keywordPlaceholder: 'Enter brand name/code/contact',
      // Brand level options
      levelInternational: 'International',
      levelNational: 'National',
      levelOrdinary: 'Standard',
      // Brand type options
      typeAnchor: 'Anchor',
      typeSubAnchor: 'Sub-Anchor',
      typeStandard: 'Standard',
      // Approval status options
      approvalPending: 'Pending',
      approvalApproved: 'Approved',
      approvalRejected: 'Rejected',
      // Cooperation status options
      coopAvailable: 'Available',
      coopIntention: 'Intention',
      coopCooperating: 'Cooperating',
      coopSuspended: 'Suspended',
      coopBlacklisted: 'Blacklisted',
      // Business conditions
      businessConditions: 'Business Conditions',
      rentTolerance: 'Rent Tolerance',
      rentToleranceUnit: 'CNY/㎡/month',
      minArea: 'Min Area',
      maxArea: 'Max Area',
      areaRange: 'Area Requirement',
      needExhaust: 'Need Exhaust',
      needPower: 'Need Separate Meter',
      powerDemand: 'Power Demand',
      powerDemandPlaceholder: 'Enter power demand description',
      // Status & Settings
      statusAndSettings: 'Status & Settings',
      basicInfo: 'Basic Info',
      contactInfo: 'Contact Info',
      businessCondition: 'Business Conditions',
      sortOrder: 'Sort Order',
      // Dialog titles
      addBrand: 'Add Brand',
      editBrand: 'Edit Brand',
      brandDetail: 'Brand Detail',
      // Actions
      approvePass: 'Approve',
      approveReject: 'Reject',
      suspendCooperation: 'Suspend Cooperation',
      resumeCooperation: 'Resume Cooperation',
      addToBlacklist: 'Add to Blacklist',
      removeFromBlacklist: 'Remove from Blacklist',
      setPreferred: 'Set as Preferred',
      cancelPreferred: 'Cancel Preferred',
      // Confirm messages
      deleteConfirm: 'Are you sure to delete this brand?',
      batchDeleteConfirm: 'Are you sure to delete the selected brands?',
      blacklistConfirm: 'Are you sure to blacklist this brand?',
      // Success messages
      approvedSuccess: 'Approved',
      rejectedSuccess: 'Rejected',
      suspendedSuccess: 'Cooperation suspended',
      resumedSuccess: 'Cooperation resumed',
      blacklistedSuccess: 'Added to blacklist',
      unblacklistedSuccess: 'Removed from blacklist',
      // Labels
      industry: 'Industry',
      // Validation
      brandNameRequired: 'Brand name is required',
      brandCodeRequired: 'Brand code is required',
      brandLevelRequired: 'Brand level is required',
      industryRequired: 'Industry is required',
      phoneInvalid: 'Please enter a valid phone number',
      emailInvalid: 'Please enter a valid email address',
      autoGenerate: 'Auto-generated',
    },

    // ==================== Merchant Info ====================
    info: {
      title: 'Merchant Info',
      merchantName: 'Merchant Name',
      merchantCode: 'Merchant Code',
      autoGenerateCode: 'Auto-generated',
      merchantLevel: 'Merchant Level',
      merchantType: 'Merchant Type',
      merchantStatus: 'Merchant Status',
      merchantDescription: 'Description',
      contactPerson: 'Contact Person',
      contactPhone: 'Contact Phone',
      contactEmail: 'Contact Email',
      foundingYear: 'Founding Year',
      headquarters: 'Headquarters',
      website: 'Website',
      isPreferred: 'Preferred',
      preferredMerchant: 'Preferred Merchant',
      alternativeMerchant: 'Alternative Merchant',
      sortOrder: 'Sort Order',
      keyword: 'Keyword',
      keywordPlaceholder: 'Enter merchant name/code/contact',
      // Level options
      levelInternational: 'International',
      levelNational: 'National',
      levelOrdinary: 'Standard',
      // Type options
      typeCompany: 'Company',
      typeIndividual: 'Individual',
      typeFranchise: 'Franchise',
      // Status options
      statusPotential: 'Potential',
      statusIntention: 'Intention',
      statusSigned: 'Signed',
      // Tab titles
      tabBasic: 'Basic Info',
      tabEnterprise: 'Enterprise Info',
      tabFinance: 'Finance Info',
      tabBusiness: 'Business Info',
      // Enterprise info
      legalPerson: 'Legal Person',
      legalPersonPlaceholder: 'Enter legal representative',
      businessLicense: 'Business License',
      registeredCapital: 'Registered Capital',
      registeredCapitalUnit: '10K CNY',
      establishedDate: 'Established Date',
      businessScope: 'Business Scope',
      registeredAddress: 'Registered Address',
      officeAddress: 'Office Address',
      // Finance info
      bankName: 'Bank Name',
      bankAccount: 'Bank Account',
      accountName: 'Account Name',
      taxNumber: 'Tax Number',
      // Business info
      relatedBrand: 'Related Brand',
      creditRating: 'Credit Rating',
      cooperationHistory: 'Cooperation History',
      cooperationHistoryPlaceholder: 'Enter cooperation history',
      // Dialog titles
      addMerchant: 'Add Merchant',
      editMerchant: 'Edit Merchant',
      merchantDetail: 'Merchant Detail',
      // Actions
      setPreferred: 'Set as Preferred',
      cancelPreferred: 'Cancel Preferred',
      // Confirm messages
      deleteConfirm: 'Are you sure to delete this merchant?',
      batchDeleteConfirm: 'Are you sure to delete the selected merchants?',
      // Labels
      industry: 'Industry',
      // Validation
      merchantNameRequired: 'Merchant name is required',
      merchantCodeRequired: 'Merchant code is required',
      merchantLevelRequired: 'Merchant level is required',
      industryRequired: 'Industry is required',
      phoneInvalid: 'Please enter a valid phone number',
      emailInvalid: 'Please enter a valid email address',
      // Success messages
      enabledSuccess: 'Enabled successfully',
      disabledSuccess: 'Disabled successfully',
    },

    // ==================== Policy Management ====================
    policy: {
      title: 'Policy Management',
      policyName: 'Policy Name',
      year: 'Year',
      resourceType: 'Resource Type',
      effectiveDate: 'Effective Date',
      expiryDate: 'Expiry Date',
      keyword: 'Keyword',
      keywordPlaceholder: 'Enter policy name',
      allYears: 'All Years',
      // Status
      statusEnabled: 'Enabled',
      statusDisabled: 'Disabled',
      // Actions
      resourcePolicy: 'Resource Policy',
      // Confirm
      deleteConfirm: 'Are you sure to delete this policy? All associated resource policies will also be deleted.',
      batchDeleteConfirm: 'Are you sure to delete the selected {count} policies?',
      // Success messages
      enableSuccess: 'Enabled successfully',
      disableSuccess: 'Disabled successfully',
      // Policy form
      addPolicy: 'Add Policy',
      editPolicy: 'Edit Policy',
      policyDetail: 'Policy Detail',
      coreInfo: 'Core Info',
      project: 'Project',
      projectPlaceholder: 'Select project',
      projectRequired: 'Please select a project',
      yearRequired: 'Please select a year',
      yearPlaceholder: 'Select year',
      policyNameRequired: 'Please enter policy name',
      policyNamePlaceholder: 'e.g. F&B Shop V202601',
      policyType: 'Policy Type',
      policyTypeRent: 'Rent Strategy',
      policyTypePromotion: 'Promotion',
      policyTypeOther: 'Other',
      // Rent strategy
      rentStrategy: 'Rent Strategy',
      rentCalculation: 'Rent Method',
      rentFixed: 'Fixed Rent',
      rentCommission: 'Commission',
      rentGuaranteedHigher: 'Guaranteed Higher',
      rentFixedPlusExcess: 'Fixed + Excess',
      standardRent: 'Standard Rent',
      commissionShort: 'Commission',
      minRent: 'Min Rent',
      maxRent: 'Max Rent',
      managementFee: 'Management Fee',
      managementFeeLabel: 'Property Mgmt Fee',
      promotionFund: 'Promotion Fund',
      commissionRate: 'Commission Rate',
      guaranteedAmount: 'Guaranteed Amount',
      excessThreshold: 'Excess Threshold',
      // Contract & incentive
      contractAndIncentive: 'Contract & Incentive',
      minLeaseYears: 'Min Years',
      minLeaseYearsLabel: 'Min Lease Years',
      maxLeaseYears: 'Max Years',
      maxLeaseYearsLabel: 'Max Lease Years',
      paymentMethod: 'Payment Method',
      paymentCycle: 'Payment Cycle',
      paymentMonthly: 'Monthly',
      paymentQuarterly: 'Quarterly',
      paymentSemiAnnual: 'Semi-Annual',
      paymentYearly: 'Yearly',
      depositMonths: 'Deposit',
      depositMonthsLabel: 'Deposit Months',
      rentFree: 'Reno Free Rent',
      rentFreePeriod: 'Renovation Free Period',
      operationFree: 'Op Free Rent',
      operationFreePeriod: 'Operation Free Period',
      decorationPeriod: 'Renovation Period',
      decorationSubsidy: 'Renovation Subsidy',
      annualIncreaseRate: 'Annual Increase',
      increaseCycle: 'Increase Cycle',
      // Special config
      specialConfig: 'Special Configuration',
      specialConfigTip: 'Select a resource type to expand specific fields. Optional — save works without it',
      assetTypeLabel: 'Resource Type',
      assetTypePlaceholder: 'Select (optional)',
      clearSpecialConfig: 'Clear Special Config',
      specialConfigUnsavedTip: 'Special configuration has unsaved data. Are you sure you want to close?',
      closeConfirm: 'Close Confirmation',
      tabBasicPolicy: 'Basic Policy',
      selectAssetTypeFirst: 'Please select a resource type to expand special configuration',
      // Form actions
      formNotReady: 'Form not ready, please try again later',
      checkRequired: 'Please check required fields',
      editFailed: 'Update failed',
      addFailed: 'Add failed',
      loadDetailFailed: 'Failed to load policy detail',
      // Units
      unitYuanPerSqmMonth: 'CNY/㎡/month',
      unitYuanPerMonth: 'CNY/month',
      unitPercent: '%',
      unitYear: 'years',
      unitMonth: 'months',
      unitYuan: 'CNY',
      unitSqm: '㎡',
      // Detail page - section titles
      sectionBasicInfo: 'Basic Info',
      sectionAssetInfo: 'Associated Shop',
      sectionRentStrategy: 'Rent Strategy',
      sectionContractIncentive: 'Contract & Incentive',
      sectionSystemInfo: 'System Info',
      // Detail page - labels
      createdBy: 'Created By',
      updatedBy: 'Updated By',
      shopLabel: 'Shop',
      areaLabel: 'Area',
      buildingLabel: 'Building',
      floorLabel: 'Floor',
      // Asset policy page
      assetPolicy: 'Resource Policy',
      baseRent: 'Base Rent',
      searchAssetPlaceholder: 'Search resource code/name',
      addResource: 'Add Resource',
      batchAdd: 'Batch Add',
      assetCode: 'Resource Code',
      assetName: 'Resource Name',
      buildingFloor: 'Building / Floor',
      areaSqm: 'Area (㎡)',
      shopType: 'Shop Type',
      comparisonBase: 'vs Baseline',
      comparisonBaseline: 'Baseline',
      addResourceDialog: 'Add Resource',
      building: 'Building',
      buildingPlaceholder: 'Select building',
      floor: 'Floor',
      floorPlaceholder: 'Select floor',
      shopTypePlaceholder: 'Shop type',
      confirmAdd: 'Confirm Add',
      selectAtLeastOne: 'Please select at least one resource',
      addResourceSuccess: '{count} resource policies added',
      addResourceFailed: 'Failed to add resource policies',
      loadParentFailed: 'Failed to load parent policy',
      loadAssetListFailed: 'Failed to load resource policy list',
      loadAvailableFailed: 'Failed to load available resources',
      deleteAssetConfirm: 'Are you sure to delete this resource policy?',
      // Shop type map
      shopTypeRetail: 'Retail',
      shopTypeFood: 'F&B',
      shopTypeEntertainment: 'Entertainment',
      shopTypeService: 'Service',
      // Asset status map
      assetStatusAvailable: 'Available',
      assetStatusRented: 'Rented',
      assetStatusMaintenance: 'Maintenance',
      assetStatusReserved: 'Reserved',
      // Asset policy form
      assetPolicyTitle: 'Edit Resource Policy',
      assetInfo: 'Resource Info',
      parentPolicyLabel: 'Parent Policy',
      assetStatus: 'Asset Status',
      saveFailed: 'Save failed',
      // Dynamic field groups (fieldGroups.js)
      fg: {
        groupBasic: 'Basic Info',
        groupRent: 'Rent Strategy',
        groupRentSimple: 'Rent Standard',
        groupManagement: 'Property Management',
        groupLeaseTerm: 'Lease Term',
        groupLeaseTermSimple: 'Rental Period',
        groupIncentive: 'Incentives',
        groupIncentiveSimple: 'Incentive Terms',
        groupIncrement: 'Rent Escalation',
        groupCoefficient: 'Coefficients',
        groupBrand: 'Brand Requirements',
        groupOperation: 'Operation Requirements',
        groupLayout: 'Business Layout',
        groupBilling: 'Billing Strategy',
        groupRestriction: 'Restrictions',
        policyName: 'Policy Name',
        policyType: 'Policy Type',
        effectiveDate: 'Effective Date',
        expiryDate: 'Expiry Date',
        rentCalculation: 'Rent Calculation',
        standardRent: 'Standard Rent',
        minRent: 'Minimum Rent',
        maxRent: 'Maximum Rent',
        commissionRate: 'Commission Rate',
        guaranteedAmount: 'Guaranteed Amount',
        excessThreshold: 'Excess Threshold',
        managementFee: 'Property Management Fee',
        promotionFund: 'Promotion Fund',
        minLeaseYears: 'Min Lease Years',
        maxLeaseYears: 'Max Lease Years',
        paymentMethod: 'Payment Method',
        depositMonths: 'Deposit Months',
        minRentalPeriod: 'Min Rental Period',
        maxRentalPeriod: 'Max Rental Period',
        rentFreePeriod: 'Renovation Free Period',
        operationFreePeriod: 'Operation Free Period',
        decorationPeriod: 'Renovation Period',
        decorationSubsidy: 'Renovation Subsidy',
        freePeriod: 'Free Period',
        annualIncreaseRate: 'Annual Increase Rate',
        increaseCycle: 'Increase Cycle',
        floorCoefficient: 'Floor Coefficient',
        positionCoefficient: 'Position Coefficient',
        rentAreaType: 'Rent Area Type',
        brandLevel: 'Brand Level Required',
        preferredBrands: 'Preferred Brands',
        alternativeBrands: 'Alternative Brands',
        minTurnover: 'Min Turnover',
        minEfficiency: 'Min Space Efficiency',
        operationHours: 'Operating Hours',
        exclusivityClause: 'Exclusivity Clause',
        businessType: 'Business Type ID',
        layoutRequirements: 'Layout Requirements',
        otherConditions: 'Other Conditions',
        billingCycle: 'Billing Cycle',
        standardPrice: 'Standard Price',
        minPrice: 'Min Price',
        maxPrice: 'Max Price',
        industryRestriction: 'Industry Restriction',
        priorityRules: 'Priority Rules',
        allowedCategories: 'Allowed Categories',
        safetyRequirements: 'Safety Requirements',
        usagePermission: 'Usage Permission',
        restrictions: 'Restrictions',
        unitYuanSqmMonth: 'CNY/㎡/month',
        unitPercent: '%',
        unitYuanMonth: 'CNY/month',
        unitYear: 'years',
        unitMonth: 'months',
        unitDay: 'days',
        unitYuan: 'CNY',
        optRentStrategy: 'Rent Strategy',
        optPromotionStrategy: 'Promotion Strategy',
        optOther: 'Other',
        optRentFixed: 'Fixed Rent',
        optRentCommission: 'Commission',
        optRentGuaranteed: 'Guaranteed Higher',
        optRentFixedPlusExcess: 'Fixed + Excess',
        optPayMonthly: 'Monthly',
        optPayQuarterly: 'Quarterly',
        optPaySemiAnnual: 'Semi-Annual',
        optPayYearly: 'Yearly',
        optAreaBuilding: 'GFA',
        optAreaUsable: 'NFA',
        optBrandIntlTop: 'International Tier 1',
        optBrandIntlSecond: 'International Tier 2',
        optBrandNatTop: 'National Tier 1',
        optBrandNatSecond: 'National Tier 2',
        optBrandRegional: 'Regional',
        optBrandAny: 'Any',
        optBillingDay: 'Daily',
        optBillingWeek: 'Weekly',
        optBillingMonth: 'Monthly',
        optBillingYear: 'Yearly',
        optBillingSession: 'Per Session',
        phPolicyName: 'Enter policy name',
        phEffectiveDate: 'Select effective date',
        phExpiryDate: 'Select expiry date',
        phPreferredBrands: 'Separate multiple brands with commas',
        phAlternativeBrands: 'Separate multiple brands with commas',
        phOperationHours: 'e.g. 10:00-22:00',
        phExclusivityClause: 'Enter exclusivity clause description',
        phBusinessTypeId: 'Enter business type ID',
        phLayoutRequirements: 'Enter layout requirements',
        phOtherConditions: 'Enter other conditions',
        phIndustryRestriction: 'Enter industry restriction details',
        phPriorityRules: 'Enter priority rules',
        phAllowedCategories: 'Enter allowed categories',
        phSafetyRequirements: 'Enter safety requirements',
        phUsagePermission: 'Enter usage permission details',
        phRestrictions: 'Enter restriction details',
      },
    },

    // ==================== Contract Management ====================
    contract: {
      title: 'Contract Management',
      contractNo: 'Contract No.',
      manualContractNo: 'Manual Contract No.',
      erpNo: 'ERP Contract No.',
      thirdMerchantId: 'Third-party Merchant ID',
      merchantName: 'Merchant Name',
      shopName: 'Asset',
      leaseArea: 'Lease Area',
      brandName: 'Brand',
      standardRent: 'Standard Rent',
      contractPeriod: 'Contract Period',
      contractStatus: 'Contract Status',
      backToList: 'Back to List',
      addContract: 'Add Contract',
      // Contract status
      statusDraft: 'Draft',
      statusPending: 'Pending',
      statusActive: 'Active',
      statusPendingEffective: 'Pending Effective',
      statusExpired: 'Expired',
      statusTerminated: 'Terminated',
      // Actions
      activate: 'Activate',
      terminate: 'Terminate',
      revoke: 'Revoke',
      submitApproval: 'Submit for Approval',
      withdrawApproval: 'Withdraw',
      approve: 'Approve',
      // Approval messages
      submitApprovalConfirm: 'Are you sure to submit this contract for approval?',
      withdrawApprovalConfirm: 'Are you sure to withdraw the approval?',
      submitApprovalSuccess: 'Submitted for approval, please wait for the result',
      submitDirectSuccess: 'Contract activated directly (no approval workflow configured)',
      withdrawSuccess: 'Approval withdrawn',
      withdrawFailed: 'Withdraw failed',
      approvalRunning: 'This contract already has a running approval process',
      // Tab
      tabApprovalHistory: 'Approval History',
      // Confirm
      deleteConfirm: 'Are you sure to delete this contract?',
      statusChangeConfirm: 'Are you sure to change the contract status to "{status}"?',
      confirmAction: 'Confirm Action',
      // Success messages
      statusUpdateSuccess: 'Status updated',
      statusUpdateFailed: 'Status update failed',
      createSuccess: 'Created successfully',
      updateSuccess: 'Updated successfully',
      loadDetailFailed: 'Failed to load contract detail',
      // Tab titles
      tabBasic: 'Basic Info',
      tabRent: 'Rent Terms',
      tabTerms: 'Contract Terms',
      // Search
      keyword: 'Keyword',
      keywordPlaceholder: 'Contract no./merchant/shop',
      merchant: 'Merchant',
      allMerchants: 'All Merchants',
      // Basic info
      basicInfo: 'Contract Basic Info',
      autoGenerate: 'Auto-generated',
      signingDept: 'Signing Dept.',
      signingPerson: 'Leasing Agent',
      merchantInfo: 'Merchant Info',
      signDate: 'Sign Date',
      meetingDate: 'GM Meeting Date',
      cosignDate: 'Co-sign Date',
      sealDate: 'Seal Date',
      freeParkingCount: 'Free Parking',
      maxParkingApply: 'Max Parking Apply',
      unitCount: '',
      signingCompany: 'Billing Entity',
      shopInfo: 'Shop Info',
      assetInfo: 'Asset Info',
      assetType: 'Asset Type',
      periodInfo: 'Contract Period',
      contractStart: 'Contract Start',
      contractEnd: 'Contract End',
      renovationStart: 'Renovation Start',
      renovationEnd: 'Renovation End',
      openingDate: 'Opening Date',
      selectDate: 'Select date',
      // Payment Collection
      paymentCollectionInfo: 'Payment Collection',
      paymentCollectionType: 'Collection Type',
      paymentCollectionUnified: 'Unified Collection',
      paymentCollectionSelf: 'Self Collection',
      businessType: 'Business Type',
      salesDept: 'Sales Department',
      category: 'Business Category',
      categoryPlaceholder: 'Select business type first',
      salesCollectionMethod: 'Sales Collection',
      salesMethodPOS: 'POS',
      salesMethodMerchant: 'Merchant Report',
      salesMethodAuto: 'Auto Collect',
      salesMethodOther: 'Other',
      // Rent terms
      rentCalculation: 'Rent Method',
      rentFixed: 'Fixed Rent',
      rentCommission: 'Commission',
      rentGuaranteedHigher: 'Guaranteed Higher',
      rentStandard: 'Rent Standard',
      commissionRate: 'Commission Rate',
      guaranteedAmount: 'Guaranteed Amount',
      managementFee: 'Management Fee',
      promotionFund: 'Promotion Fund',
      freeRentAndIncrease: 'Free Rent & Increase',
      rentFreePeriod: 'Renovation Free Period',
      operationFreePeriod: 'Operation Free Period',
      annualIncreaseRate: 'Annual Increase',
      increaseCycle: 'Increase Cycle',
      depositAndPayment: 'Deposit & Payment',
      deposit: 'Deposit',
      depositMonths: 'Deposit Months',
      paymentMethod: 'Payment Method',
      paymentMonthly: 'Monthly',
      paymentQuarterly: 'Quarterly',
      paymentSemiAnnual: 'Semi-Annual',
      paymentYearly: 'Yearly',
      // Contract terms
      specialTerms: 'Special Terms',
      specialTermsPlaceholder: 'Enter special terms',
      attachments: 'Contract Attachments',
      attachment: 'Attachment',
      uploadAttachment: 'Upload',
      uploadTip: 'Supports common document and image formats, max 20MB per file, up to 10 attachments',
      noAttachment: 'No attachments',
      uploadSuccess: '{name} uploaded',
      uploadFailed: '{name} upload failed',
      fileSizeLimit: 'File size cannot exceed 20MB',
      fileCountLimit: 'Maximum 10 attachments',
      // Validation
      merchantRequired: 'Please select a merchant',
      shopRequired: 'Please select a shop',
      startDateRequired: 'Please select a start date',
      endDateRequired: 'Please select an end date',
      deptRequired: 'Please select a signing department',
      signPersonRequired: 'Please select a recruiter',
      businessTypeRequired: 'Please select a business type',
      categoryRequired: 'Please select a category',
      salesDeptRequired: 'Please select a sales department',
      brandRequired: 'Please select at least one brand',
      signCompanyRequired: 'Please select a billing entity',
      openingDateRequired: 'Please select an expected opening date',
      // Units
      unitYuanPerSqmMonth: 'CNY/㎡/month',
      unitYuanPerMonth: 'CNY/month',
      unitPercent: '%',
      unitMonth: 'months',
      unitYear: 'years',
      unitYuan: 'CNY',
      unitSqm: '㎡',
      selectedCount: 'Selected',
      refPriceHint: 'Ref. Price (from asset archive)',
      periodTo: 'to',
      // Shop import
      policyDataImported: 'Pricing data imported from shop policy',
      shopDataImported: 'Basic pricing imported from shop',
      // Create from intention
      createFromIntention: 'Create from Intention',
      createFromIntentionSuccess: 'Contract draft created from intention',
      selectIntention: 'Select Intention',
      selectIntentionHint: 'Select an active intention to auto-create a contract draft with its data',
      intentionNo: 'Intention No.',
      intentionMerchant: 'Merchant',
      intentionBrand: 'Brand',
      intentionShop: 'Shop',
      intentionRent: 'Proposed Rent',
      intentionArea: 'Proposed Area',
      intentionPeriod: 'Proposed Period',
      noActiveIntention: 'No active intentions',
      searchIntentionPlaceholder: 'Search intention no./merchant/brand/shop',
      confirmCreateFromIntention: 'Create a contract draft from this intention?',
      // Period config tab
      tabPeriod: 'Period Config',
      // Contract Amendment
      submitAmendment: 'Submit Amendment',
      originalContract: 'Original Contract',
      shop: 'Shop',
      rentInfo: 'Rent Info',
      periodInfo: 'Period Info',
      amendmentType: 'Amendment Type',
      amendmentTypeRent: 'Rent Adjustment',
      amendmentTypeRate: 'Rate Adjustment',
      amendmentTypeTerm: 'Term Adjustment',
      amendmentTypeFee: 'Fee Adjustment',
      amendmentTypeComprehensive: 'Comprehensive Amendment',
      effectiveDate: 'Effective Date',
      effectiveDateRequired: 'Please select effective date',
      amendmentReason: 'Reason',
      amendmentReasonPlaceholder: 'Enter amendment reason',
      amendmentContent: 'Amendment Content',
      signPerson: 'Signatory',
      signCompany: 'Billing Entity',
      periodEdit: 'Period Edit',
      periodTitle: 'Period {num}',
      periodName: 'Period Name',
      startDate: 'Start Date',
      endDate: 'End Date',
      increaseRate: 'Increase Rate',
      changeSummary: 'Change Summary',
      amendmentStatusDraft: 'Draft',
      amendmentStatusPending: 'Pending Approval',
      amendmentStatusApproved: 'Approved',
      amendmentStatusRejected: 'Rejected',
      submitAmendmentConfirm: 'Submit this contract amendment for approval?',
      submitAmendmentSuccess: 'Amendment submitted for approval',
      revokeAmendment: 'Revoke Amendment',
      revokeAmendmentConfirm: 'Revoke this contract amendment?',
      revokeAmendmentSuccess: 'Amendment revoked',
      tabBasic: 'Basic Info',
      tabRent: 'Rent Terms',
      tabTerms: 'Contract Terms',

      // ==================== Period Configuration ====================
      period: {
        // Tips
        tipTitle: 'Instructions:',
        tipContent: '1. Click "Split by Natural Year" or "Split by Contract Year" to auto-generate periods, then adjust manually; 2. Configure time, rent, fees, free rent period and payment method for each period; 3. Monthly breakdown distinguishes between natural month and contract month.',
        // Toolbar
        configTitle: 'Period Config',
        contractPeriod: 'Contract Period',
        totalYears: '{years} years total',
        splitNaturalYear: 'Split by Natural Year',
        splitContractYear: 'Split by Contract Year',
        addPeriod: 'Add Period Manually',
        // Period card
        yearTitle: 'Year {num}',
        firstNonFull: 'First year non-full',
        newPeriod: 'New Period',
        copy: 'Copy',
        months: 'months',
        monthsUnit: 'mo',
        increaseTag: 'Increase',
        freeRentTag: 'Free rent {count}',
        // Time settings
        timeSetting: 'Time Settings',
        periodName: 'Period Name',
        periodNamePlaceholder: 'e.g., First Year',
        startDate: 'Start Date',
        endDate: 'End Date',
        // Rent settings
        rentSetting: 'Rent Settings',
        rentCalcType: 'Rent Method',
        rentFixed: 'Fixed Rent',
        rentCommission: 'Commission Rent',
        rentOrCommission: 'Fixed or Commission (Higher)',
        rentPlusCommission: 'Fixed + Commission',
        commissionRate: 'Commission Rate',
        commissionCalcType: 'Commission Calc Type',
        commissionFlat: 'Flat Rate',
        commissionTiered: 'Tiered',
        commissionFlatHint: 'Match tier by sales, apply that rate to entire sales amount',
        commissionTieredHint: 'Calculate each tier separately and sum up',
        commissionTiers: 'Tiered Commission Config',
        tierIndex: 'Tier',
        salesStart: 'Sales Start',
        salesEnd: 'Sales End',
        addCommissionTier: '+ Add Tier',
        guaranteedAmount: 'Guaranteed Amount',
        rentCalcDimension: 'Calculation Dimension',
        dimensionSqMonthly: 'Unit price /㎡/mo (× area)',
        dimensionSqDaily: 'Unit price /㎡/day (× area × days)',
        dimensionYearly: 'Unit price /㎡/yr (÷12)',
        dimensionMonthly: 'Monthly rent total (CNY/mo)',
        dimensionDaily: 'Daily amount (CNY/day × days)',
        standardRent: 'Rent Standard',
        standardRentUnitPrice: 'Unit Price',
        standardRentFixedAmount: 'Rent Amount',
        monthlyRentInput: 'Monthly Rent',
        monthlyRent: 'Equivalent Monthly Rent',
        monthlyRentUnit: 'CNY/mo',
        unitSqmMonthly: 'CNY/㎡/mo',
        unitSqmDaily: 'CNY/㎡/day',
        unitSqmYearly: 'CNY/㎡/yr',
        unitFixedMonthly: 'CNY/mo',
        unitFixedDaily: 'CNY/day',
        increaseSetting: 'Increase Settings',
        noIncrease: 'No Increase',
        hasIncrease: 'With Increase',
        increaseMethod: 'Increase Method',
        increasePercent: 'Fixed Percentage',
        increaseFixed: 'Fixed Amount',
        increaseRate: 'Increase Rate',
        increaseAmount: 'Increase Amount',
        increaseHint: 'Increase is a period-level coefficient, subsequent periods compound',
        increaseLastHint: 'Last period does not need increase settings (increase applies to next period)',
        calcHint: 'Calculation:',
        calcHintContent: 'Monthly rent (total) is converted by dimension. For ㎡/mo unit price: total = unit × area = {rent} × {area}',
        // Other fees
        otherFees: 'Other Fees (auto-created fee items are pre-filled)',
        feeName: 'Fee Name',
        feeCode: 'Fee Code',
        feeCalcType: 'Calc Method',
        feeStartDate: 'Start Date',
        feeEndDate: 'End Date',
        feeAmount: 'Amount/Price',
        feeHasIncrease: 'Include in Increase',
        addFee: 'Add',
        selectFeeItems: 'Select fee items (multi-select)',
        managementFee: 'Management Fee',
        promotionFund: 'Promotion Fee',
        unitSqMonthly: 'CNY/㎡/mo',
        unitMonthly: 'CNY/mo',
        unitOnce: 'One-time',
        unitSalesPercent: 'Sales %',
        feeAmountPercent: 'Rate (%)',
        // Free rent
        freeRentSetting: 'Free Rent Settings (within this period)',
        to: 'to',
        waiveFees: 'Waive Fees:',
        rent: 'Rent',
        addFreeRent: '+ Add Free Rent Period',
        // Payment settings
        paymentSetting: 'Payment Settings',
        paymentCycle: 'Payment Cycle',
        cycleMonthly: 'Monthly',
        cycleQuarterly: 'Quarterly',
        cycleSemiAnnual: 'Semi-Annual',
        cycleYearly: 'Yearly',
        billTiming: 'Bill Timing',
        timingCurrent: 'Current Period',
        timingAdvance1: '1 Month in Advance',
        timingAdvance2: '2 Months in Advance',
        billDay: 'Bill Day',
        firstMonth: 'First Month',
        secondMonth: 'Second Month',
        day: '',
        paymentDays: 'Payment Due',
        daysWithin: 'days',
        lateFeeEnabled: 'Late Fee Enabled',
        enabled: 'Enabled',
        disabled: 'Disabled',
        lateFeeRate: 'Late Fee Rate',
        lateFeeCap: 'Late Fee Cap',
        graceDays: 'Grace Period',
        optional: 'Optional',
        billDayHint: 'Bill Day Info:',
        billDayHintContent: '"Current" bills on the specified day within the fee period month (e.g., April fee billed on Apr 25); "Advance" bills one month prior (e.g., April fee billed on Mar 25); "Next" bills one month after (e.g., April fee billed on May 25). Selecting "End of Month" uses the last day of the month automatically',
        // Operations
        noPeriods: 'No period configuration',
        noPeriodsHint: 'Click buttons above to auto-split or add period manually',
        selectContractDateFirst: 'Please select contract start and end dates first',
        splitSuccess: 'Period split successfully',
        copySuccess: 'Period copied successfully',
        deleteConfirm: 'Are you sure to delete this period?',
        validateNoPeriods: 'Please configure contract periods first',
        validatePeriodName: 'Period name is required',
        validatePeriodDates: 'Please set period start and end dates',
        validateRentCalcType: 'Please select a rent calculation type',
        validateStandardRent: 'Please enter the standard rent',
        validatePaymentCycle: 'Please select a payment cycle',
        validateBillTiming: 'Please select a bill timing',
        validatePaymentDays: 'Please enter payment days',
        validatePeriodFailed: 'Period #{index} has missing required fields',
        syncFirstPeriod: 'Sync First',
        syncFirstPeriodConfirm: 'Sync first period settings to all other periods? (Only dates and names will be preserved)',
        syncSuccess: 'First period settings synced to other periods',
      },

      // ==================== Monthly Fee Breakdown ====================
      monthlyFee: {
        title: 'Monthly Fee Breakdown',
        tipTitle: 'Instructions:',
        tipContent: 'Click "Generate Breakdown" to auto-generate monthly fee breakdown based on period configuration. Supports daily proration for non-full months, free rent marking, and increase calculation.',
        summaryHint: '{months} periods, total {amount} CNY',
        generate: 'Generate Breakdown',
        regenerate: 'Regenerate',
        exportExcel: 'Export Excel',
        yearMonth: 'Month',
        periodNum: 'Period',
        dateRange: 'Date Range',
        billDate: 'Bill Date',
        rent: 'Rent',
        managementFee: 'Mgmt Fee',
        promotionFund: 'Promo Fund',
        deposit: 'Deposit',
        totalAmount: 'Total',
        remark: 'Remark',
        freeTag: 'Free',
        nonFullMonth: 'Non-full',
        hasFreePeriod: 'Free Rent',
        freeLegend: 'Free rent fees',
        increaseLegend: 'Increased months',
        commissionSettled: 'Settled',
        commissionPending: 'Pending Sales',
        commissionLegend: 'Commission rent settled with sales data',
        recalculateCommission: 'Recalculate',
        recalculateSuccess: 'Commission recalculated, {count} records updated',
        nonFullMonthLegend: 'Non-full month prorated',
        noData: 'No monthly fee breakdown',
        noDataHint: 'Please configure periods first, then click "Generate Breakdown"',
        noContractPeriod: 'Please set contract period first',
        generateSuccess: 'Monthly fee breakdown generated',
        generateFailed: 'Generation failed',
        exportSuccess: 'Export successful',
        exportFailed: 'Export failed',
        exportNeedSave: 'Please save the contract before exporting',
        total: 'Total',
        tabTitle: 'Monthly',
        // Fee entry
        addFee: 'Add Fee',
        batchAddFee: 'Batch Entry',
        importFee: 'Batch Import',
        downloadTemplate: 'Download Template',
        feeItem: 'Fee Item',
        feeType: 'Fee Type',
        feeTypeNormal: 'Normal',
        feeTypeAdjust: 'Adjustment',
        feeTypeReduce: 'Reduction',
        feeTypeSupplement: 'Supplement',
        feeSource: 'Fee Source',
        feeSourceContract: 'Contract Generated',
        feeSourceManual: 'Manual Entry',
        generateConfirm: 'Confirm to generate monthly fee breakdown?',
        regenerateConfirm: 'Regenerating will overwrite existing data. Continue?',
        invalidAmount: 'Please enter a valid amount',
        manualModified: ' manually modified',
        noPeriodsWarning: 'Please configure contract periods first',
        // Fee entry form
        inputAmount: 'Fee Amount',
        inputAmountPlaceholder: 'Positive=add, Negative=reduce',
        billDateLabel: 'Bill Date',
        dueDateLabel: 'Due Date',
        saveAndClose: 'Save & Close',
        saveAndGenerate: 'Save & Generate Bill',
        addFeeSuccess: 'Fee added successfully',
        addFeeFailed: 'Failed to add fee',
        // Batch entry
        selectContracts: 'Select Contracts/Merchants',
        filterByFloor: 'Filter by Floor',
        filterByBusinessType: 'Filter by Business Type',
        searchMerchant: 'Search Merchant',
        selectedContracts: '{count} contracts selected',
        amountMode: 'Amount Mode',
        amountModeFixed: 'Fixed Amount',
        amountModeRatio: 'By Ratio',
        amountModeArea: 'By Area',
        amountModeFixedPlaceholder: 'Fixed amount per contract (negative for reduction)',
        amountModeRatioPlaceholder: 'Percentage of current rent (e.g. -10 means 10% reduction)',
        amountModeAreaPlaceholder: 'Amount per sqm (negative for reduction)',
        batchPreview: 'Will add fees for {count} contracts, total: {amount} CNY',
        batchAddSuccess: 'Batch entry successful',
        batchAddFailed: 'Batch entry failed',
        // Excel import
        importStep1: 'Download Template',
        importStep2: 'Fill Data',
        importStep3: 'Upload Import',
        importStep4: 'Validation Result',
        selectFile: 'Select File',
        validating: 'Validating...',
        importResult: 'Import Result',
        validRows: 'Valid',
        invalidRows: 'Invalid',
        viewErrors: 'View Errors',
        confirmImport: 'Confirm Import',
        importSuccess: 'Import successful',
        importFailed: 'Import failed',
        importProgress: 'Success: {success}, Failed: {fail}',
        generateBillOnImport: 'Generate bills after import',
        // Table columns
        calcType: 'Calc Method',
        originalAmount: 'Original Amount',
        actualAmount: 'Actual Receivable',
        isFree: 'Free',
        increase: 'Increase',
        days: 'Days',
        // Summary cards
        totalOriginal: 'Total Receivable',
      },

      // ==================== Renewal & Amendment ====================
      // Contract type
      contractType: 'Contract Type',
      typeNew: 'New',
      typeRenewal: 'Renewal',
      typeAmendment: 'Amendment',
      contractVersion: 'Version',
      // Renewal
      renew: 'Renew',
      renewContract: 'Initiate Renewal',
      renewConfirm: 'Are you sure to initiate a renewal based on this contract? A new renewal draft will be created.',
      renewSuccess: 'Renewal draft created',
      renewFailed: 'Renewal failed',
      renewalCompare: 'Renewal Comparison',
      renewalOriginal: 'Original',
      renewalOriginalValue: 'Original',
      renewalNewValue: 'Renewal',
      compareField: 'Term',
      compareChange: 'Change',
      expandCompare: 'Expand',
      collapseCompare: 'Collapse',
      compareAnalysis: 'Compare Analysis',
      compareHint: 'Click the button to load comparison with original contract',
      originalContract: 'Original Contract',
      rentInfo: 'Rent Information',
      periodInfo: 'Period Information',
      periodEdit: 'Period Edit',
      renewalContract: 'Renewal Contract',
      changeRate: 'Change',
      noChange: '--',
      // Structured comparison sections
      feeImpactTitle: 'Fee Impact Analysis',
      termChangesTitle: 'Contract Terms Changes',
      otherChangesTitle: 'Other Information Changes',
      fieldLabel: 'Item',
      oldValueLabel: 'Original',
      newValueLabel: 'New',
      oldTotal: 'Original Total',
      newTotal: 'New Total',
      impact: 'Difference',
      noFeeChanges: 'No fee changes',
      noTermChanges: 'No term changes',
      noOtherChanges: 'No other changes',
      totalIncrease: 'Increase',
      totalDecrease: 'Decrease',
      perUnit: 'Unit Price',
      contractTotal: 'Contract Total',
      // Amendment
      amend: 'Amend',
      amendment: 'Contract Amendment',
      createAmendment: 'Initiate Amendment',
      createAmendmentConfirm: 'Are you sure to initiate an amendment for this contract?',
      createAmendmentSuccess: 'Amendment created',
      amendmentNo: 'Amendment No.',
      amendmentType: 'Amendment Type',
      amendmentTypeRent: 'Rent Adjustment',
      amendmentTypeRate: 'Rate Adjustment',
      amendmentTypeTerm: 'Term Change',
      amendmentTypeFee: 'Fee Adjustment',
      amendmentTypeComprehensive: 'Comprehensive',
      canOnlyExtend: 'Can only extend end date in amendment mode',
      amendmentReason: 'Reason',
      amendmentReasonPlaceholder: 'Enter amendment reason',
      effectiveDate: 'Effective Date',
      effectiveDateRequired: 'Please select effective date',
      amendmentStatus: 'Amendment Status',
      amendmentStatusDraft: 'Draft',
      amendmentStatusPending: 'Pending',
      amendmentStatusApproved: 'Approved',
      amendmentStatusRejected: 'Rejected',
      originalValue: 'Original',
      newValue: 'New Value',
      fieldChanged: 'Changed',
      saveDraft: 'Save Draft',
      submitAmendment: 'Submit for Approval',
      submitAmendmentConfirm: 'Are you sure to submit this amendment for approval?',
      submitAmendmentSuccess: 'Amendment submitted for approval',
      deleteAmendmentConfirm: 'Are you sure to delete this amendment draft?',
      deleteAmendmentSuccess: 'Amendment draft deleted',
      updateAmendmentSuccess: 'Amendment saved',
      amendmentFields: 'Amendment Fields',
      amendmentContent: 'Amendment Content',
      changeSummary: 'Change Summary',
      amendmentApplied: 'Amendment applied',
      hasActiveAmendment: 'This contract already has an active amendment',
      // History
      history: 'History',
      contractHistory: 'Contract History',
      versionTimeline: 'Version Timeline',
      actionInitial: 'Initial Archive',
      actionAmendment: 'Amendment Archive',
      actionRenewal: 'Renewal Archive',
      viewSnapshot: 'View Snapshot',
      compare: 'Compare',
      snapshotDetail: 'History Snapshot',
      versionCompare: 'Version Compare',
      currentVersion: 'Current Version',
      historyVersion: 'History Version',
      noHistory: 'No history versions',
      // Contract chain
      contractChain: 'Contract Chain',
      currentContract: 'Current',
      // Status - archived
      statusArchived: 'Archived',
    },

    // ==================== Payment Plan ====================
    paymentPlan: {
      title: 'Payment Plan',
      totalPlan: 'Total Plans',
      totalAmount: 'Total Amount',
      paidAmount: 'Paid Amount',
      generatePlan: 'Generate Plan',
      overdueAlert: '{count} overdue payments',
      showAll: 'Show All',
      showOverdue: 'Overdue Only',
      periodNum: 'Period',
      billDate: 'Bill Date',
      dueDate: 'Due Date',
      periodRange: 'Period',
      planAmount: 'Plan Amount',
      paidAmountLabel: 'Received',
      lateFee: 'Late Fee',
      overdueDays: 'Overdue',
      days: 'days',
      registerPay: 'Register',
      registerPayTitle: 'Register Payment',
      unpaidAmount: 'Unreceived',
      inputPayAmount: 'Received Amount',
      inputLateFee: 'Late Fee Paid',
      generateConfirm: 'This will overwrite existing plans. Continue?',
      generateSuccess: 'Payment plan generated',
      paySuccess: 'Payment registered',
      statusPending: 'Planned',
      statusPaid: 'Paid',
      statusPartial: 'Partial',
      statusOverdue: 'Overdue',
      // New fields
      regenerateConfirm: 'Regenerating will overwrite existing plans. Continue?',
      hasPaidWarning: 'Cannot regenerate: some bills have been paid',
      noMonthlyFeeWarning: 'Please generate monthly fee breakdown first',
      refreshOverdue: 'Refresh Overdue Status',
      overdueUpdated: 'Overdue status updated',
      generateManualPlan: 'Generate Bill Manually',
      generateManualSuccess: 'Bill generated successfully',
      generateManualFailed: 'Failed to generate bill',
      selectFeeToGenerate: 'Please select fees to generate bill',
      // Payment dialog
      payRemark: 'Payment Remark',
      payRemarkPlaceholder: 'Enter payment remark',
      payAmountExceed: 'Payment amount exceeds due amount. Confirm as prepayment?',
      payConfirm: 'Confirm Payment',
    },

    // ==================== Billing Management ====================
    billing: {
      title: 'Billing Management',
      // Table columns
      billNo: 'Bill No.',
      contractNo: 'Contract No.',
      merchantName: 'Merchant',
      brandName: 'Brand',
      assetName: 'Asset',
      planAmount: 'Receivable Amount',
      paidAmount: 'Received Amount',
      lateFee: 'Late Fee',
      overdueDays: 'Overdue Days',
      dueDate: 'Due Date',
      periodRange: 'Period',
      periodNum: 'Period',
      status: 'Status',
      billStatus: 'Bill Status',
      operation: 'Action',
      // Summary cards
      totalPlan: 'Total Receivable',
      totalPaid: 'Total Received',
      remaining: 'Remaining',
      totalOverdue: 'Overdue Amount',
      overdueCount: 'Overdue Count',
      totalLateFee: 'Total Late Fee',
      // Search & filter
      searchPlaceholder: 'Contract No./Merchant',
      dateRangeLabel: 'Due Date Range',
      // Actions
      exportBill: 'Export Bills',
      printBill: 'Print Bills',
      refreshOverdue: 'Refresh Overdue',
      // Overdue summary
      topOverdueContracts: 'Top Overdue',
      overdueAmount: 'Overdue Amount',
      // Status
      statusPlan: 'Plan',
      statusUnpaid: 'Unpaid',
      statusPending: 'Pending',
      statusPaid: 'Paid',
      statusPartial: 'Partial',
      statusOverdue: 'Overdue',
      statusPendingDesc: 'Pending',
      statusPaidDesc: 'Paid in Full',
      statusOverdueDesc: 'Overdue',
      // Bill type filter
      billType: 'Bill Type',
      typeBill: 'Formal Bill',
      typePlan: 'Plan Record',
      // Generate bill
      generateBill: 'Generate Bill',
      generateBillConfirm: 'Confirm to convert this plan record into a formal bill?',
      generateBillSuccess: 'Bill generated successfully',
      generateBillFailed: 'Failed to generate bill',
      planTag: 'Plan Record',
      // Detail
      viewDetail: 'View',
      billDetail: 'Bill Detail',
      basicInfoSection: 'Basic Information',
      monthlyFeeSection: 'Fee Breakdown',
      feeSource: 'Source',
      feeSourceContract: 'Contract',
      feeSourceManual: 'Manual',
      feeSourcePlan: 'Plan Converted',
      noFeeData: 'No fee records',
      // Fee adjustment
      adjustFee: 'Adjust',
      adjustTitle: 'Bill Fee Adjustment',
      adjustType: 'Adjustment Type',
      adjustIncrease: 'Increase',
      adjustDecrease: 'Decrease',
      adjustAmount: 'Adjustment Amount',
      adjustReason: 'Reason',
      adjustReasonPlaceholder: 'Enter adjustment reason',
      adjustAmountMin: 'Amount must be greater than 0',
      adjustSuccess: 'Fee adjusted successfully',
      adjustFailed: 'Failed to adjust fee',
      adjustWarning: 'After decrease, due amount will be less than paid amount',
      currentPlanAmount: 'Current Due',
      currentUnpaid: 'Unpaid Balance',
      afterAdjustAmount: 'After Adjustment',
      // Fee entry
      addFee: 'Add Fee',
      addFeeTitle: 'Add Temporary Fee',
      addFeeSuccess: 'Fee added successfully',
      addFeeFailed: 'Failed to add fee',
      targetBill: 'Target Bill',
      feeItemLabel: 'Fee Item',
      feeAmount: 'Amount',
      // More actions
      more: 'More',
      printSingle: 'Print',
      printBillTitle: 'Bill Print',
      // Fee table columns
      feeItemName: 'Fee Item',
      feeType: 'Fee Type',
      originalAmount: 'Original',
      actualAmount: 'Actual',
      yearMonth: 'Year-Month',
      isFreeLabel: 'Free',
      // Regenerate
      regenerateBill: 'Regenerate',
      regenerateConfirm: 'Regenerate this bill? It will recalculate all linked fees.',
      regenerateSuccess: 'Bill regenerated successfully',
      regenerateFailed: 'Failed to regenerate bill',
      // Confirm bill
      confirmBill: 'Confirm Bill',
      confirmBillAction: 'Confirm',
      confirmBillTip: 'Once confirmed, the bill will be locked and fees cannot be modified.',
      confirmBillSuccess: 'Bill confirmed',
      confirmBillFailed: 'Failed to confirm bill',
      // Approval workflow
      submitApproval: 'Submit Approval',
      submitApprovalConfirm: 'Submit this bill for two-level approval (Operation → Finance)?',
      submitApprovalSuccess: 'Bill submitted for approval',
      submitApprovalFailed: 'Failed to submit approval',
      viewApprovalProgress: 'View Approval Progress',
      noApprovalProgress: 'No approval progress available',
      approvalProgress: 'Approval Progress',
      approvalRecords: 'Approval Records',
      noApprovalRecords: 'No approval records',
      operator: 'Approver',
      comment: 'Comment',
      stepSubmit: 'Submit',
      stepOperationApproval: 'Operation Approval',
      stepFinanceApproval: 'Finance Approval',
      stepComplete: 'Complete',
      // REVIEWING status
      statusReviewing: 'Under Review',
      // CONFIRMED status
      statusConfirmed: 'Confirmed',
      statusMerchantConfirmed: 'Merchant Confirmed',
      // Fee table columns (for invoice selection)
      feeName: 'Fee Item',
      amount: 'Amount',
      // Manual bill generation
      manualGenerate: 'Manual Generate',
      manualGenerateTitle: 'Manual Bill Generation',
      selectContract: 'Select Contract',
      searchContractPlaceholder: 'Search by contract no. or merchant name',
      planListTitle: 'Payment Plan List',
      noPlanData: 'No payment plans for this contract',
      noSelectablePlan: 'No operable plan records',
      selectedCount: '{count} period(s) selected',
      selectedTotal: 'Total Amount',
      billConfirmed: 'Confirmed',
      billPaid: 'Paid',
      canRegenerate: 'Regenerate',
      pendingGenerate: 'Pending',
      manualGenerateConfirm: 'Confirm to generate (or regenerate) bills for the selected {count} period(s)?',
      manualGenerateSuccess: 'Successfully processed {count} bill(s)',
      manualGenerateFailed: 'Batch bill generation failed',
    },

    // ==================== Fee Entry ====================
    feeEntry: {
      title: 'Fee Entry',
      addFee: 'Add Fee',
      excelImport: 'Import Excel',
      linkToBill: 'Link to Bill',
      generateBill: 'Generate Bill',
      batchDelete: 'Batch Delete',
      contractNo: 'Contract No.',
      merchantName: 'Merchant',
      yearMonth: 'Year-Month',
      feeSource: 'Source',
      feeSourceManual: 'Manual',
      feeSourceImported: 'Excel Import',
      feeSourceRenovation: 'Renovation',
      feeItemName: 'Fee Item',
      feeType: 'Fee Type',
      amount: 'Amount',
      linkedBill: 'Linked Bill',
      linked: 'Linked',
      unlinked: 'Unlinked',
      createTime: 'Created',
      operation: 'Action',
      // Entry dialog
      addTitle: 'Add Fee',
      editTitle: 'Edit Fee',
      selectContract: 'Select Contract',
      searchContractPlaceholder: 'Search by contract no. or merchant',
      selectFeeItems: 'Fee Items',
      chargeMonth: 'Charge Month',
      chargeMonthPlaceholder: 'Select the month to charge',
      amountDetail: 'Amount Detail',
      billDate: 'Bill Date',
      dueDate: 'Due Date',
      saveSuccess: 'Fee saved successfully',
      saveFailed: 'Failed to save fee',
      // Link to bill dialog
      linkTitle: 'Link to Bill',
      linkTip: 'Link {count} fees to the following bill',
      noAvailableBill: 'No pending bills available for this contract',
      createNewBill: 'Create New Bill',
      linkSuccess: 'Fees linked to bill',
      linkFailed: 'Failed to link',
      generateSuccess: 'Bill generated successfully',
      generateFailed: 'Failed to generate',
      // Delete
      deleteConfirm: 'Delete {count} selected fees?',
      deleteSuccess: 'Deleted successfully',
      deleteFailed: 'Failed to delete',
      // Validation
      selectFeeFirst: 'Please select fee records first',
      onlyUnlinkedCanOperate: 'Can only operate on unlinked fees',
      feesMustSameContract: 'Selected fees must belong to the same contract',
      // Status
      statusDraft: 'Draft',
      statusConfirmed: 'Confirmed',
      statusPending: 'Billed',
      statusPartial: 'Partial',
      statusPaid: 'Paid',
      statusOverdue: 'Overdue',
      statusFilter: 'Status',
      // Audit
      confirmFee: 'Confirm',
      confirmFeeConfirm: 'Confirm {count} selected fees? Confirmed fees cannot be deleted.',
      confirmFeeSuccess: '{count} fees confirmed',
      confirmFeeFailed: 'Failed to confirm',
      onlyDraftCanDelete: 'Confirmed fees cannot be deleted',
      onlyConfirmedCanOperate: 'Only confirmed fees can be linked or billed',
      // Refund
      refundable: 'Refundable',
      nonRefundable: 'Non-refundable',
      refundStatusNone: 'Not Refunded',
      refundStatusPending: 'Pending Refund',
      refundStatusRefunded: 'Refunded',
      refundFee: 'Refund',
      refundAmount: 'Refund Amount',
      refundRemark: 'Refund Remark',
      refundConfirm: 'Confirm refund for this fee?',
      refundSuccess: 'Refund successful',
      refundFailed: 'Refund failed',
      isRefundable: 'Refundable',
    },

    // ==================== Negotiation Management ====================
    negotiation: {
      title: 'Negotiation Management',
      // Negotiation methods
      methodVisit: 'On-site Visit',
      methodPhone: 'Phone Call',
      methodOnline: 'Online',
      methodCall: 'Phone',
      methodFace: 'In-person',
      methodWechat: 'WeChat',
      methodEmail: 'Email',
      methodOther: 'Other',
      // Negotiation results
      resultContinue: 'Follow Up',
      resultNegotiating: 'Negotiating',
      resultSigned: 'Signed',
      resultTerminated: 'Terminated',
      resultVisited: 'Visited',
      resultInterested: 'Interested',
      resultNoInterest: 'Not Interested',
      resultPending: 'Pending',
      resultIntention: 'Interested',
      resultNoIntention: 'Not Interested',
      resultReached: 'Reached',
      resultNone: 'Not Reached',
      resultPreliminary: 'Preliminary',
      resultFull: 'Full',
      // Labels
      method: 'Method',
      date: 'Date',
      location: 'Location',
      responsible: 'Responsible',
      participants: 'Participants',
      content: 'Content',
      merchantDemand: 'Merchant Demand',
      ourProposal: 'Our Proposal',
      nextPlan: 'Next Plan',
      result: 'Result',
      successRate: 'Success Rate',
      followUpDate: 'Follow-up Date',
      nextFollowUp: 'Next Follow-up',
      // Form
      addRecord: 'Add Negotiation Record',
      recordDetail: 'Negotiation Detail',
      currentShop: 'Current Shop',
      selectMerchant: 'Select merchant',
      selectDate: 'Select date',
      contentPlaceholder: 'Enter negotiation content',
      remarkPlaceholder: 'Enter remark',
      // Validation
      merchantRequired: 'Please select a merchant',
      methodRequired: 'Please select a method',
      dateRequired: 'Please select a date',
      contentRequired: 'Please enter the content',
      // Success messages
      addSuccess: 'Negotiation record added',
      addFailed: 'Failed to add negotiation record',
      loadDetailFailed: 'Failed to load negotiation detail',
      // Section titles
      sectionBasic: 'Basic Info',
      sectionContent: 'Negotiation Content',
      sectionResult: 'Negotiation Result',
      // Merchant panel
      unknownMerchant: 'Unknown Merchant',
      noRecords: 'No negotiation records',
      // Dialog titles
      addNegotiation: 'Add Negotiation Record',
      editNegotiation: 'Edit Negotiation Record',
      negotiationDetail: 'Negotiation Detail',
      // Shop
      shop: 'Shop',
      shopPlaceholder: 'Select shop',
      // Form labels
      merchantId: 'Merchant',
      merchantIdPlaceholder: 'Select merchant',
      locationPlaceholder: 'Enter location',
      responsiblePlaceholder: 'Enter responsible person',
      participantsPlaceholder: 'Enter participants',
      merchantDemandPlaceholder: 'Enter merchant demand',
      ourProposalPlaceholder: 'Enter our proposal',
      nextPlanPlaceholder: 'Enter next plan',
      // Confirm
      deleteConfirm: 'Are you sure to delete this negotiation record?',
      batchDeleteConfirm: 'Are you sure to delete the selected negotiation records?',
      // Validation
      merchantRequired: 'Please select a merchant',
      merchantIdRequired: 'Please select a merchant',
      brand: 'Brand',
      createIntention: 'Create Intention',
      batchCreateSuccess: 'Successfully created {count} negotiation records',
      addFailed: 'Failed to create negotiation record',
      // Negotiation round
      round: 'Round',
      roundFirst: 'First',
      roundSecond: 'Second',
      roundFinal: 'Final',
      roundPlaceholder: 'Select round',
      // Location type
      locationType: 'Location Type',
      locationTypeOnSite: 'On-site',
      locationTypeMeetingRoom: 'Meeting Room',
      locationTypeOther: 'Other',
      locationTypePlaceholder: 'Select location type',
      // Viewing info
      sectionViewing: 'Viewing Info',
      hasViewing: 'Includes property viewing',
      viewingShops: 'Viewed Shops',
      viewingShopsPlaceholder: 'Select viewed shops',
      viewingResult: 'Viewing Result',
      viewingResultVeryInterested: 'Very Interested',
      viewingResultGeneral: 'General',
      viewingResultNotInterested: 'Not Interested',
      viewingResultPlaceholder: 'Select viewing result',
      siteEvaluation: 'Site Evaluation',
      siteEvaluationPlaceholder: 'Enter merchant evaluation on location, area, conditions',
      viewingRemark: 'Viewing Remark',
      viewingRemarkPlaceholder: 'Enter viewing remark',
      // Core negotiation
      sectionCore: 'Core Negotiation',
      ourOffer: 'Our Offer',
      ourOfferPlaceholder: 'Enter our offer (rent, mgmt fee, guarantee, commission, free period, term, etc.)',
      disputes: 'Disputes',
      disputesPlaceholder: 'Enter main disagreements',
      agreementStatus: 'Agreement Status',
      agreementNone: 'No Agreement',
      agreementPreliminary: 'Preliminary',
      agreementFull: 'Full Agreement',
      agreementPending: 'Pending',
      agreementAgreed: 'Agreed',
      agreementStatusPlaceholder: 'Select agreement status',
      // Follow-up
      sectionFollowUp: 'Result & Follow-up',
      nextAction: 'Next Action',
      nextActionPlaceholder: 'Enter next action',
    },

    // ==================== Intention Management ====================
    intention: {
      title: 'Intention Management',
      // Status
      statusActive: 'Active',
      statusExpired: 'Expired',
      statusCancelled: 'Cancelled',
      statusConverted: 'Converted',
      // Labels
      shop: 'Shop',
      merchant: 'Merchant',
      brand: 'Brand',
      signingCompany: 'Billing Entity',
      proposedRent: 'Proposed Rent',
      rentCalculation: 'Rent Method',
      proposedArea: 'Proposed Area',
      managementFee: 'Management Fee',
      commissionRate: 'Commission Rate',
      guaranteedAmount: 'Guaranteed Amount',
      contractPeriod: 'Contract Period',
      freePeriod: 'Free Period',
      annualIncrease: 'Annual Increase',
      deposit: 'Deposit',
      paymentMethod: 'Payment Method',
      intentionFeeStatus: 'Intention Fee Status',
      intentionFeePaid: 'Fee Paid',
      businessPlan: 'Business Plan',
      specialRequirements: 'Special Requirements',
      suggestedRent: 'Suggested Rent',
      suggestedArea: 'Suggested Area',
      // Rent methods
      rentFixed: 'Fixed Rent',
      rentCommission: 'Commission',
      rentGuaranteed: 'Guaranteed Higher',
      // Payment methods
      payMonthly: 'Monthly',
      payQuarterly: 'Quarterly',
      payYearly: 'Yearly',
      // Fee status
      feeNotPaid: 'Not Paid',
      feePaid: 'Paid',
      feeRefunded: 'Refunded',
      // Form
      createIntention: 'Create Intention',
      sectionSelectShop: 'Select Shop',
      sectionMerchantInfo: 'Merchant Info',
      sectionLeaseTerms: 'Lease Terms',
      sectionContractPeriod: 'Contract Period',
      sectionFeeInfo: 'Fee Info',
      sectionOther: 'Other',
      // Shop status labels
      shopRented: 'Rented',
      shopSigned: 'Signed',
      shopIntention: 'Intention',
      shopNegotiating: 'Negotiating',
      shopReserved: 'Reserved',
      shopMaintenance: 'Maintenance',
      shopVacant: 'Vacant',
      // Multi-select tip
      selectedShops: '{count} shops selected, an intention record will be created for each',
      selectShopPlaceholder: 'Select shops (multiple)',
      shopPlaceholder: 'Select shop',
      // Placeholders
      merchantPlaceholder: 'Select merchant',
      brandPlaceholder: 'Select brand',
      companyPlaceholder: 'Enter billing entity name',
      rentPlaceholder: 'CNY/㎡/month',
      areaPlaceholder: '㎡',
      percentPlaceholder: '%',
      amountPlaceholder: 'CNY/month',
      monthPlaceholder: 'months',
      yuanPlaceholder: 'CNY',
      planPlaceholder: 'Enter business plan',
      requirementsPlaceholder: 'Enter special requirements',
      // Validation
      shopRequired: 'Please select at least one shop',
      merchantRequired: 'Please select a merchant',
      rentRequired: 'Please enter proposed rent',
      startDateRequired: 'Please select a start date',
      endDateRequired: 'Please select an end date',
      // Success messages
      batchCreateSuccess: '{count} intentions created',
      createSuccess: 'Intention created',
      createFailed: 'Failed to create intention',
      createPartialFailed: '{count} intentions failed to create',
      cancelSuccess: 'Intention cancelled',
      cancelFailed: 'Failed to cancel intention',
      noRecords: 'No intention records',
      // Actions
      cancelIntention: 'Cancel',
      createFormalPlacement: 'Create Formal Placement',
      convertToContract: 'Convert to Contract',
      cancelIntentionConfirm: 'Are you sure to cancel this intention? This cannot be undone.',
      cancelIntentionTitle: 'Cancel Intention',
      confirmCancel: 'Confirm Cancel',
      // Tab titles
      tabBasic: 'Basic Info',
      tabLeaseTerms: 'Lease Terms',
      tabNegotiation: 'Negotiation Records',
      tabConfirmation: 'Confirmation',
      tabDeposit: 'Deposit',
      // Workbench statistics
      workbenchTitle: 'Negotiation & Intention Workbench',
      statsPendingFollow: 'Pending Follow-up',
      statsConfirming: 'Confirming',
      statsDepositPaid: 'Deposit Paid',
      statsReadyToLand: 'Ready to Land',
      // Negotiation timeline
      negotiationTimeline: 'Negotiation Records',
      noNegotiationRecord: 'No negotiation records',
      viewAllNegotiations: 'View All',
      addNegotiationRecord: 'Add Record',
      // Confirmation
      confirmationStatus: 'Confirmation Status',
      confirmationDate: 'Confirmation Date',
      confirmIntention: 'Confirm Intention',
      submitConfirm: 'Submit for Confirmation',
      approveConfirm: 'Approve',
      rejectConfirm: 'Reject',
      submitConfirmSuccess: 'Submitted for confirmation',
      approveConfirmSuccess: 'Approved successfully',
      rejectConfirmSuccess: 'Rejected successfully',
      submitConfirmMsg: 'Are you sure to submit this intention for confirmation?',
      approveConfirmMsg: 'Are you sure to approve this intention confirmation?',
      rejectConfirmMsg: 'Please enter rejection reason',
      rejectConfirmTitle: 'Reject Intention Confirmation',
      // Confirmation status options
      confirmStatusDraft: 'Draft',
      confirmStatusPending: 'Pending',
      confirmStatusConfirmed: 'Confirmed',
      confirmStatusRejected: 'Rejected',
      confirmStatusVoided: 'Voided',
      // Deposit
      depositAmount: 'Deposit Amount',
      depositPaid: 'Paid Amount',
      depositStatus: 'Deposit Status',
      payDeposit: 'Pay Deposit',
      refundDeposit: 'Refund Deposit',
      // Detail drawer
      detailDrawer: 'Intention Detail',
      printIntention: 'Print Intention',
      noCustomTemplate: 'No print template configured. Please configure in System > Print Template Management.',
      // Filters
      filterShop: 'Shop',
      filterBusinessType: 'Business Type',
      filterBrand: 'Brand',
      filterAllStatus: 'All Status',
      businessTypeAll: 'All Business Types',
      brandAll: 'All Brands',
      shopAll: 'All Shops',
      // List labels
      intentionNo: 'Intention No.',
      intentionDate: 'Intention Date',
      expiryDate: 'Expiry Date',
      leasePeriod: 'Lease Period',
      freePeriodMonths: 'Free Period (months)',
      assetType: 'Asset Type',
      // Dialog titles
      addIntention: 'Add Intention',
      editIntention: 'Edit Intention',
      intentionDetail: 'Intention Detail',
      // Confirm
      deleteConfirm: 'Are you sure to delete this intention?',
      batchDeleteConfirm: 'Are you sure to delete the selected intentions?',
      convertConfirm: 'Are you sure to convert this intention to a contract?',
      convertTitle: 'Confirm Conversion',
      convertSuccess: 'Conversion successful',
      // Search
      keyword: 'Keyword',
      keywordPlaceholder: 'Enter intention no./merchant/brand name',
      dateRange: 'Intention Date',
      dateRangeTo: 'to',
      // Form
      proposedStartDate: 'Proposed Start',
      proposedEndDate: 'Proposed End',
      startDatePlaceholder: 'Select start date',
      endDatePlaceholder: 'Select end date',
      intentionDatePlaceholder: 'Select intention date',
      expiryDatePlaceholder: 'Select expiry date',
      // Validation
      brandRequired: 'Please select a brand',
      intentionDateRequired: 'Please select intention date',
      proposedRentRequired: 'Please enter proposed rent',
      // Table column labels
      merchantName: 'Merchant Name',
      brandName: 'Brand Name',
      shopName: 'Asset Name',
      statusLabel: 'Status',
      signCompany: 'Billing Entity',
      signCompanyPlaceholder: 'Enter billing entity',
      rentFreePeriod: 'Rent-Free Period',
      annualIncreaseRate: 'Annual Increase Rate',
      specialReqs: 'Special Requirements',
      businessPlanPlaceholder: 'Enter business plan',
      specialReqsPlaceholder: 'Enter special requirements',
      intentionDepositStatus: 'Deposit Status',
      // Intention Deposit sub-module
      deposit: {
        feeConfig: 'Fee Configuration',
        feeName: 'Fee Name',
        feeCode: 'Fee Code',
        feeItem: 'Fee Item',
        selectFeeItem: 'Select fee item',
        amount: 'Amount',
        remark: 'Remark',
        totalAmount: 'Total Amount',
        paymentRecords: 'Payment Records',
        receivePayment: 'Receive Payment',
        recordNo: 'Record No.',
        paymentMethod: 'Payment Method',
        paymentTime: 'Payment Time',
        payee: 'Payee',
        recordStatus: 'Status',
        recordDetail: 'Record Detail',
        // Payment methods
        cash: 'Cash',
        bankTransfer: 'Bank Transfer',
        alipay: 'Alipay',
        wechat: 'WeChat',
        check: 'Check',
        // Record status
        statusPending: 'Pending',
        statusReceived: 'Received',
        statusDeducted: 'Deducted',
        statusRefundPending: 'Refunding',
        statusRefunded: 'Refunded',
        // Statistics
        receivedAmount: 'Received',
        availableDeduct: 'Available for Deduction',
        // Actions
        deduct: 'Deduct',
        refund: 'Refund',
        refundApplied: 'Refund applied',
        // Dialog fields
        deductAmount: 'Deduct Amount',
        availableAmount: 'Available Amount',
        refundAmount: 'Refund Amount',
        refundableAmount: 'Refundable Amount',
        refundReason: 'Refund Reason',
        deductedAmount: 'Deducted Amount',
        effectiveTime: 'Effective Time',
        // System config related
        systemConfig: 'System Config',
        feeConfigTip: 'Fee items are configured centrally. Go to "System Management > Fee Item Management" to modify',
        calcType: 'Calc Type',
        calcTypeSqMonthly: '/㎡/month',
        calcTypeFixedMonthly: '/month',
        calcTypeFixedOnce: 'One-time',
        calcTypePercent: 'Percentage',
        defaultValue: 'Default',
      },
    },

    // ==================== Plan Management ====================
    plan: {
      title: 'Plan Management',
      planName: 'Plan Name',
      planType: 'Plan Type',
      targetPeriod: 'Target Period',
      targetRentRate: 'Target Lease Rate',
      targetIncome: 'Target Revenue',
      targetTotalIncome: 'Target Total Revenue',
      responsible: 'Responsible',
      participants: 'Participants',
      businessTypeTargets: 'Business Type Targets',
      businessTypeTargetsPlaceholder: 'Business type ratio targets, e.g.: F&B 30%, Retail 40%, Entertainment 20%, Other 10%',
      keyTasks: 'Key Tasks',
      planDescription: 'Plan Description',
      approvalComments: 'Approval Comments',
      approver: 'Approver',
      approvalDate: 'Approval Date',
      approvalStatus: 'Approval Status',
      startDate: 'Start Date',
      endDate: 'End Date',
      // Types
      typeAnnual: 'Annual Plan',
      typeQuarter: 'Quarterly Plan',
      typeMonthly: 'Monthly Plan',
      // Approval status
      statusDraft: 'Draft',
      statusPending: 'Pending Approval',
      statusApproved: 'Approved',
      statusRejected: 'Rejected',
      // Dialog titles
      addPlan: 'Add Leasing Plan',
      editPlan: 'Edit Leasing Plan',
      planDetail: 'Leasing Plan Detail',
      // Validation
      planNameRequired: 'Plan name is required',
      planTypeRequired: 'Plan type is required',
      startDateRequired: 'Start date is required',
      endDateRequired: 'End date is required',
      // Confirm
      deleteConfirm: 'Are you sure to delete this plan?',
    },

    // ==================== Task Management ====================
    task: {
      title: 'Task Management',
      taskName: 'Task Name',
      taskType: 'Task Type',
      taskStatus: 'Task Status',
      assignedTo: 'Assigned To',
      deadline: 'Deadline',
      progress: 'Progress',
      targetIncome: 'Target Revenue',
      targetMerchantCount: 'Target Merchants',
      targetAssetType: 'Target Asset Type',
      targetAssetTypePlaceholder: 'e.g.: Shop/Office/Parking',
      taskDescription: 'Task Description',
      taskRequirements: 'Task Requirements',
      relatedPlan: 'Related Plan',
      relatedPlanPlaceholder: 'Select related plan',
      completionDate: 'Completion Date',
      // Types
      typeMerchant: 'Leasing',
      typeNegotiation: 'Negotiation',
      typeContract: 'Contract',
      typeOther: 'Other',
      // Status
      statusPending: 'Pending',
      statusInProgress: 'In Progress',
      statusCompleted: 'Completed',
      statusCancelled: 'Cancelled',
      statusOverdue: 'Overdue',
      // Completion
      completionResult: 'Completion Result',
      // Dialog titles
      addTask: 'Add Leasing Task',
      editTask: 'Edit Leasing Task',
      taskDetail: 'Leasing Task Detail',
      completeTaskTitle: 'Complete Task',
      // Actions
      startExecution: 'Start',
      completeTask: 'Complete',
      cancelTask: 'Cancel Task',
      confirmComplete: 'Confirm Complete',
      // Validation
      taskNameRequired: 'Task name is required',
      taskTypeRequired: 'Task type is required',
      deadlineRequired: 'Deadline is required',
      assignedToRequired: 'Assigned person is required',
      // Confirm
      deleteConfirm: 'Are you sure to delete this task?',
      cancelConfirm: 'Are you sure to cancel this task?',
      // Success messages
      taskStarted: 'Task started',
      taskCompleted: 'Task completed',
      taskCancelled: 'Task cancelled',
    },

    // ==================== Workbench ====================
    workbench: {
      title: 'Leasing Workbench',
      // Canvas
      noAssetData: 'No asset data, please select a floor',
      selectBuilding: 'Select building',
      selectFloor: 'Select floor',
      // Work phases
      phaseAnalytics: 'Analytics',
      phaseProgress: 'Execution',
      // View mode
      viewList: 'List',
      viewCanvas: 'Canvas',
      // Toolbar
      filter: 'Filter',
      exitFullscreen: 'Exit',
      fullscreen: 'Fullscreen',
      searchAssetName: 'Search asset name',
      assetType: 'Asset Type',
      assetTypeShop: 'Shop',
      assetTypeOfficeSpace: 'Office Space',
      assetTypeParking: 'Parking',
      assetTypeSharedSpace: 'Shared Space',
      assetTypeWarehouse: 'Warehouse',
      assetTypeAdvertising: 'Advertising',
      businessTypeFilter: 'Business Type',
      progressFilter: 'Progress',
      clearFilters: 'Clear Filters',
      // Business type options
      businessRetail: 'Retail',
      businessFood: 'F&B',
      businessEntertainment: 'Entertainment',
      businessService: 'Service',
      businessNone: 'Unplanned',
      // Progress status options
      progressEntered: 'Occupied',
      progressSigned: 'Signed',
      progressActive: 'Intention',
      progressNegotiating: 'Negotiating',
      progressVacant: 'Vacant',
      // Panel
      assetDetail: 'Asset Detail',
      businessPlanning: 'Business Planning',
      // Panel titles
      basicInfo: 'Basic Info',
      policyInfo: 'Leasing Policy',
      contractInfo: 'Contract Info',
      intentionInfo: 'Intention Info',
      negotiationRecords: 'Negotiation Records',
      placementInfo: 'Formal Placement',
      simulatedPlacementInfo: 'Simulated Placement',
      noSimulatedPlacement: 'No simulated placement records',
      lifecycle: 'Business Progress Chain',
      // Description labels
      assetCode: 'Asset Code',
      shopStatus: 'Asset Status',
      merchantProgress: 'Leasing Progress',
      businessTypePlanning: 'Business Planning',
      selectBusinessType: 'Select business type',
      policyName: 'Policy Name',
      selectPolicy: 'Select leasing policy',
      changePolicy: 'Change leasing policy',
      noPolicy: 'No policy linked',
      // Shop status
      statusRented: 'Rented',
      statusAvailable: 'Available',
      statusReserved: 'Reserved',
      statusMaintenance: 'Maintenance',
      statusOperating: 'Operating',
      statusPendingExit: 'Pending Exit',
      noContract: 'No contract',
      // Contract status
      contractDraft: 'Draft',
      contractPending: 'Pending',
      contractActive: 'Active',
      contractExpired: 'Expired',
      contractTerminated: 'Terminated',
      // Intention status
      intentionConverted: 'Converted',
      intentionActive: 'Active',
      intentionExpired: 'Expired',
      intentionCancelled: 'Cancelled',
      // Buttons
      signContract: 'Sign',
      convert: 'Convert',
      createIntention: 'Create Intention',
      renovation: 'Renovation',
      // Approval
      approvalPending: '{count} approvals pending',
      approvalInProgress: 'Approval in progress',
      // Statistics charts
      statisticsTitle: 'Statistics',
      chartProgress: 'Leasing Progress',
      chartBusinessType: 'Business Type Distribution',
      chartLeaseRate: 'Floor Lease Rate',
      chartArea: 'Area Statistics',
      totalArea: 'Total Area',
      rentedArea: 'Rented Area',
      vacantArea: 'Vacant Area',
      leaseRate: 'Lease Rate',
      expandCharts: 'Expand Charts',
      collapseCharts: 'Collapse Charts',
      // Legend
      legendTitle: 'Legend',
      legendTotal: 'Total',
      // Context menu
      noAvailableActions: 'No available actions',
      // Lifecycle
      lifecycleCompleted: 'Completed',
      lifecycleInProgress: 'In Progress',
      lifecyclePending: 'Pending',
      lifecycleApprovalPending: 'Approval in progress, please wait for the result',
      lifecycleCompletionTime: 'Completed: ',
      // Success messages
      policyLinked: 'Policy linked',
      policyLinkFailed: 'Failed to link policy',
      currentBusinessType: 'Current Business Type',
      changeBusinessType: 'Change Business Type',
      setBusinessTypePlaceholder: 'Select business type',
      businessTypeLinked: 'Business type set',
      businessTypeLinkFailed: 'Failed to set business type',
      businessTypeSet: 'Business type set',
      businessTypeCleared: 'Business type cleared',
      businessTypeFailed: 'Failed to set business type',
      createIntentionFromNeg: 'Create Intention',
      // List view columns
      colName: 'Asset Name',
      colCode: 'Asset Code',
      colArea: 'Area(㎡)',
      colStatus: 'Asset Status',
      colBusinessType: 'Business Type',
      colMerchant: 'Merchant',
      colBrand: 'Brand',
      colProgressStatus: 'Leasing Status',
      // Progress status
      progressRenovating: 'Renovating',
      // Asset Archive
      archive: {
        title: '{name} Asset Archive',
        openArchive: 'Asset Archive',
        area: 'Area',
        currentStatus: 'Current Status',
        totalMerchants: 'Total Merchants',
        merchantsUnit: 'merchants',
        totalVacantDays: 'Total Vacant',
        daysUnit: 'days',
        avgLeaseYears: 'Avg Lease',
        yearsUnit: 'years',
        remainingDays: 'Remaining',
        timeline: 'Historical Timeline',
        expiryWarning: 'Current contract expires in {days} days. Consider renewal or re-leasing.',
        noRecords: 'No historical records',
        operating: 'Operating',
        vacant: 'Vacant',
        renovating: 'Renovating',
        reserved: 'Reserved',
        statusActive: 'Active',
        statusUpcoming: 'Upcoming',
        eventContract: 'Contract',
        eventVacancy: 'Vacancy',
        eventRenovation: 'Renovation',
        eventExit: 'Exit',
        eventIntention: 'Intention',
        contractNo: 'Contract No.',
        rentAmount: 'Monthly Rent',
        monthlySales: 'Avg Monthly Sales',
        exitReason: 'Exit Reason',
        month: 'mo',
      },
    },

    // ==================== Dialog Components ====================
    dialogs: {
      // Contract form dialog
      contractFormTitle: 'Sign Contract - Create from Intention',
      contractFormAlert: 'A contract draft will be automatically created from the current intention, with terms auto-filled.',
      contractDraftCreated: 'Contract draft created',
      contractDraftSuccess: 'Contract draft created successfully',
      contractCreateFailed: 'Failed to create contract',
      noValidIntention: 'No valid intention found',
      viewContractDetail: 'View Contract',
      confirmCreateContract: 'Create Contract',
      // Intention convert dialog
      convertTitle: 'Intention Conversion',
      convertAlertApproval: 'Convert intention to contract. This requires approval and will enter the approval workflow.',
      convertAlertDirect: 'The conversion will be executed directly after confirmation.',
      submitApproval: 'Submit for Approval',
      confirmConvert: 'Confirm Conversion',
      convertApprovalSubmitted: 'Submitted for approval, please wait for the result',
      convertSuccess: 'Conversion successful',
      convertFailed: 'Operation failed',
      noValidIntentionForConvert: 'No valid intention',
      // Merchant entry dialog
      entryTitle: 'Merchant Entry',
      entryAlertApproval: 'Confirm merchant entry. This requires approval and will enter the approval workflow.',
      entryAlertDirect: 'After confirmation, the asset status will change to rented.',
      entryDate: 'Entry Date',
      confirmEntry: 'Confirm Entry',
      entryApprovalSubmitted: 'Submitted for approval, please wait for the result',
      entrySuccess: 'Merchant entry successful',
      entryFailed: 'Operation failed',
      // Merchant exit dialog
      exitTitle: 'Merchant Exit',
      exitAlertApproval: 'Confirm merchant exit. This requires approval and will enter the approval workflow.',
      exitAlertDirect: 'After confirmation, the asset will become vacant and related intentions will be closed.',
      exitDate: 'Exit Date',
      exitReason: 'Exit Reason',
      confirmExit: 'Confirm Exit',
      exitDateRequired: 'Please select exit date',
      exitReasonRequired: 'Please select exit reason',
      exitApprovalSubmitted: 'Submitted for approval, please wait for the result',
      exitSuccess: 'Merchant exit successful',
      exitFailed: 'Operation failed',
      // Exit reason options
      exitReasonExpired: 'Contract Expired',
      exitReasonEarlyTermination: 'Early Termination',
      exitReasonBreach: 'Breach of Contract',
      exitReasonNegotiated: 'Negotiated Exit',
      exitReasonOther: 'Other',
      // Policy select dialog
      policySelectTitle: 'Set Leasing Policy',
      selectPolicy: 'Select Policy',
      policySelectPlaceholder: 'Select a leasing policy',
      policyRequired: 'Please select a policy',
      policySetSuccess: 'Policy linked successfully',
      policySetFailed: 'Failed to set policy',
      // Quick merchant dialog
      quickMerchantTitle: 'Quick Add Merchant',
      merchantCreated: 'Merchant created successfully',
      merchantCreateFailed: 'Failed to create merchant',
      merchantNameLabel: 'Merchant Name',
      merchantCodeLabel: 'Merchant Code',
      merchantLevelLabel: 'Merchant Level',
      industryLabel: 'Industry',
      contactPersonLabel: 'Contact Person',
      contactPhoneLabel: 'Contact Phone',
      contactEmailLabel: 'Contact Email',
      foundingYearLabel: 'Founding Year',
      headquartersLabel: 'Headquarters',
      merchantDescLabel: 'Description',
      websiteLabel: 'Website',
      preferredLabel: 'Preferred',
      alternativeLabel: 'Alternative',
      merchantNamePlaceholder: 'Enter merchant name',
      merchantCodePlaceholder: 'Enter merchant code',
      contactPersonPlaceholder: 'Enter contact person',
      contactPhonePlaceholder: 'Enter contact phone',
      contactEmailPlaceholder: 'Enter email',
      headquartersPlaceholder: 'Enter headquarters address',
      merchantDescPlaceholder: 'Enter merchant description',
      websitePlaceholder: 'Enter website URL',
      merchantNameRequired: 'Merchant name is required',
      merchantCodeRequired: 'Merchant code is required',
      merchantLevelRequired: 'Merchant level is required',
      industryRequired: 'Industry is required',
      phoneFormatError: 'Please enter a valid phone number',
      emailFormatError: 'Please enter a valid email address',
      // Quick brand dialog
      quickBrandTitle: 'Quick Create Brand',
      brandCreated: 'Brand created successfully',
      brandCreateFailed: 'Failed to create brand',
      brandTypeAnchor: 'Anchor',
      brandTypeSubAnchor: 'Sub-Anchor',
      brandTypeStandard: 'Standard',
      levelInternational: 'International Brand',
      levelNational: 'National Brand',
      levelOrdinary: 'Ordinary Merchant',
      industryCatering: 'Catering',
      industryRetail: 'Retail',
      industryEntertainment: 'Entertainment',
      industryService: 'Service',
      industryChildren: 'Children',
      industryOther: 'Other',
      // Intention form dialog
      intentionFormTitle: 'Create Leasing Intention',
      intentionEditTitle: 'Edit Leasing Intention',
      selectShop: 'Select Shop',
      shopLabel: 'Shop',
      shopPlaceholder: 'Select shops (multiple)',
      shopRequired: 'Please select at least one shop',
      batchTip: '{count} shops selected, an intention record will be created for each',
      merchantInfo: 'Merchant Info',
      merchantLabel: 'Merchant',
      merchantPlaceholder: 'Select merchant',
      merchantRequired: 'Please select a merchant',
      brandLabel: 'Brand',
      brandPlaceholder: 'Select brand',
      signCompanyLabel: 'Billing Entity',
      signCompanyPlaceholder: 'Enter billing entity name',
      leaseTerms: 'Lease Terms',
      proposedRent: 'Proposed Rent',
      proposedRentRequired: 'Please enter proposed rent',
      rentCalculation: 'Rent Calculation',
      rentFixed: 'Fixed Rent',
      rentCommission: 'Commission',
      rentGuaranteedHigher: 'Guaranteed Higher',
      proposedArea: 'Proposed Area',
      managementFee: 'Management Fee',
      commissionRate: 'Commission Rate',
      guaranteedAmount: 'Guaranteed Amount',
      contractPeriod: 'Contract Period',
      startDate: 'Start Date',
      startDateRequired: 'Please select start date',
      endDate: 'End Date',
      endDateRequired: 'Please select end date',
      rentFreePeriod: 'Rent-Free Period',
      annualIncreaseRate: 'Annual Increase Rate',
      feeInfo: 'Fee Information',
      addFeeItem: 'Add Fee Item',
      feeItemName: 'Fee Item',
      feeItemAmount: 'Amount (CNY)',
      feeItemRefundable: 'Refundable',
      feeItemRemark: 'Remark',
      feeItemSelectPlaceholder: 'Select fee item',
      feeItemTotal: 'Fee Total',
      feeItemEmpty: 'No fee items. Click "Add Fee Item" to start.',
      feeItemDuplicate: 'This fee item has been added already',
      depositAmount: 'Deposit',
      paymentMethod: 'Payment Method',
      payMonthly: 'Monthly',
      payQuarterly: 'Quarterly',
      payYearly: 'Yearly',
      intentionDepositStatus: 'Intention Deposit Status',
      depositUnpaid: 'Unpaid',
      depositPaid: 'Paid',
      depositRefunded: 'Refunded',
      depositPaidAmount: 'Deposit Paid',
      otherInfo: 'Other',
      businessPlanLabel: 'Business Plan',
      businessPlanPlaceholder: 'Enter business plan',
      specialRequirements: 'Special Requirements',
      specialRequirementsPlaceholder: 'Enter special requirements',
      intentionCreateSuccess: 'Intention created successfully',
      intentionEditSuccess: 'Intention updated successfully',
      intentionBatchCreateSuccess: '{count} intentions created successfully',
      intentionCreateFailed: 'Failed to create intention',
      selectDatePlaceholder: 'Select date',
      // Shop progress labels
      progressRented: 'Rented',
      progressSigned: 'Signed',
      progressIntention: 'Intention',
      progressNegotiating: 'Negotiating',
      progressReserved: 'Reserved',
      progressMaintenance: 'Maintenance',
      progressVacant: 'Vacant',
      // Negotiation form dialog
      negotiationFormTitle: 'Add Negotiation Record',
      currentShop: 'Current Shop',
      negotiationType: 'Negotiation Method',
      negotiationTypePhone: 'Phone',
      negotiationTypeFace: 'Face to Face',
      negotiationTypeWechat: 'WeChat',
      negotiationTypeEmail: 'Email',
      negotiationTypeOther: 'Other',
      negotiationDate: 'Negotiation Date',
      negotiationContent: 'Content',
      negotiationContentPlaceholder: 'Enter negotiation content',
      negotiationResult: 'Result',
      resultPending: 'Pending Follow-up',
      resultInterested: 'Interested',
      resultNotInterested: 'Not Interested',
      resultReached: 'Reached',
      resultNone: 'Not Reached',
      resultPreliminary: 'Preliminary',
      resultFull: 'Full',
      nextFollowDate: 'Next Follow-up',
      negotiationTypeRequired: 'Please select negotiation method',
      negotiationDateRequired: 'Please select negotiation date',
      negotiationContentRequired: 'Please enter negotiation content',
      negotiationCreateSuccess: 'Negotiation record created',
      negotiationCreateFailed: 'Failed to create negotiation record',
      // Negotiation detail dialog
      negotiationDetailTitle: 'Negotiation Detail',
      detailBasicInfo: 'Basic Info',
      detailNegotiationType: 'Method',
      detailNegotiationDate: 'Date',
      detailLocation: 'Location',
      detailNegotiator: 'Negotiator',
      detailParticipants: 'Participants',
      detailContentSection: 'Content',
      detailMerchantRequirements: 'Merchant Requirements',
      detailProposedSolution: 'Our Proposal',
      detailNextSteps: 'Next Steps',
      detailResultSection: 'Result',
      detailProbability: 'Success Rate',
      detailFollowUpDate: 'Follow-up Date',
      detailResultContinue: 'Continue',
      detailResultNegotiate: 'Negotiating',
      detailResultSign: 'Signed',
      detailResultTerminate: 'Terminated',
      detailTypeVisit: 'On-site Visit',
      detailTypePhone: 'Phone Call',
      detailTypeOnline: 'Online Meeting',
      detailLoadFailed: 'Failed to load negotiation detail',
      // Contract info labels
      contractNo: 'Contract No.',
      contractTerm: 'Contract Term',
      standardRent: 'Standard Rent',
    },

    // ==================== Business Plan ====================
    businessPlan: {
      title: 'Business Planning',
      // Filters
      project: 'Project',
      yearLabel: 'Year',
      projectPlaceholder: 'Select project',
      yearPlaceholder: 'Select year',
      // Tabs
      tabProjectPlan: 'Project Business Plan',
      tabFloorPlan: 'Floor Business Assignment',
      // Stat cards
      totalShops: 'Total Shops',
      totalArea: 'Total Area (m²)',
      assignedBusinessType: 'Assigned Types',
      businessTypeCoverage: 'Type Coverage',
      // Table columns
      colBusinessType: 'Business Type',
      colTargetPercent: 'Target %',
      colMinPercent: 'Min %',
      colMaxPercent: 'Max %',
      colActualPercent: 'Actual %',
      colAreaSqm: 'Area (m²)',
      colShopCount: 'Shops',
      colDeviation: 'Deviation',
      colTarget: 'Target%',
      colMin: 'Min%',
      colMax: 'Max%',
      colActual: 'Actual%',
      // Floor filters
      building: 'Building',
      floor: 'Floor',
      buildingPlaceholder: 'Select building',
      floorPlaceholder: 'Select floor',
      // Buttons
      exitFullscreen: 'Exit Fullscreen',
      fullscreen: 'Fullscreen',
      initRatio: 'Initialize Ratios',
      initialize: 'Initialize',
      refreshActual: 'Refresh Actual',
      batchSave: 'Batch Save',
      copyFromProject: 'Copy from Project',
      resetToAverage: 'Reset to Average',
      // Summary
      targetTotal: 'Target Total:',
      actualTotal: 'Actual Total:',
      overLimit: 'Over Limit: {count}',
      overLimitShort: 'Over Limit: {count}',
      footerTotal: 'Total',
      // Empty states
      noDataInit: 'No data, please initialize first',
      selectProjectFirst: 'Please select a project first',
      noDataCopy: 'No data, please copy from project or initialize',
      selectBuildingFloor: 'Please select a building and floor',
      noDataPlain: 'No ratio data',
      // Alert
      areaWarning: 'This project has no leasable area configured. Actual ratios will be calculated based on shop area sum. Please configure leasable area in Organization Management first.',
      // Confirm
      initConfirm: 'Initialization will generate default ratios from the business type library. Existing data will not be affected. Continue?',
      initTitle: 'Confirm Initialization',
      copyConfirm: 'Copying from project level will overwrite current floor ratios. Continue?',
      copyTitle: 'Confirm Copy',
      resetConfirm: 'Are you sure to reset to average distribution? This will overwrite current configuration.',
      resetTitle: 'Confirm Reset',
      totalNotHundred: 'Current target total is {total}%, not equal to 100%. Save anyway?',
      totalCheckTitle: 'Total Validation',
      targetMustBeHundred: 'Target total must be 100%',
      // Messages
      selectProjectMsg: 'Please select a project first',
      selectFloorMsg: 'Please select a floor first',
      initSuccess: 'Initialized successfully',
      initFailed: 'Initialization failed',
      refreshSuccess: 'Actual ratios refreshed',
      refreshFailed: 'Failed to refresh actual ratios',
      saveSuccess: 'Saved successfully',
      saveFailed: 'Save failed',
      copySuccess: 'Copied from project successfully',
      copyFailed: 'Copy failed',
      resetSuccess: 'Reset to average successfully',
      resetFailed: 'Reset failed',
      loadProjectFailed: 'Failed to load project ratios',
      loadFloorFailed: 'Failed to load floor ratios',
      developingMsg: 'Project-level ratios in development',
      addRow: 'Add',
      addDialogTitle: 'Add Business Type Ratio',
      selectBusinessType: 'Select business type',
      selectBusinessTypeFirst: 'Please select a business type',
      addSuccess: 'Added successfully',
      addFailed: 'Failed to add',
      deleteFailed: 'Delete failed',
      deleteConfirm: 'Are you sure you want to delete this ratio?',
      // BusinessPlanningPanel specific
      projectRatio: 'Project Level',
      floorRatio: 'Floor Level',
      loadRatioFailed: 'Failed to load ratio data',
      refreshStats: 'Refresh Stats',
    },

    // ==================== Industry Options (shared) ====================
    industry: {
      food: 'F&B',
      retail: 'Retail',
      entertainment: 'Entertainment',
      service: 'Service',
      children: 'Children',
      other: 'Other',
    },

    // ==================== Placement ====================
    placement: {
      totalShops: 'Total Shops',
      placedCount: 'Placed Shops',
      placedBrandCount: 'Placed Brands',
      placedRate: 'Placement Rate',
      businessTypeProgress: 'Business Type Placement Progress',
      building: 'Building',
      selectBuilding: 'Select building',
      floor: 'Floor',
      selectFloor: 'Select floor',
      businessType: 'Business Type',
      businessTypePlaceholder: 'Enter business type name',
      placementStatus: 'Placement Status',
      all: 'All',
      placed: 'Placed',
      unplaced: 'Unplaced',
      brandLevel: 'Brand Level',
      brandLevelPlaceholder: 'Brand level',
      keyword: 'Keyword',
      keywordPlaceholder: 'Shop/Brand/Merchant name',
      shopName: 'Asset Name',
      buildingName: 'Building',
      floorName: 'Floor',
      area: 'Area(m2)',
      businessTypeName: 'Business Type',
      brand: 'Brand',
      noBrand: 'None',
      merchant: 'Merchant',
      proposedRent: 'Proposed Rent',
      unitYuan: 'CNY',
      statusPlaced: 'Placed',
      statusUnplaced: 'Unplaced',
      statusConverted: 'Converted',
      statusCancelled: 'Cancelled',
      brandInternational: 'International',
      brandNational: 'National',
      brandRegional: 'Regional',
      brandLocal: 'Local',
      operation: 'Action',
      assign: 'Assign',
      replace: 'Replace',
      clear: 'Clear',
      assignTitle: 'Assign Placement',
      shop: 'Shop',
      selectBrand: 'Select brand',
      selectBusinessType: 'Select business type',
      newBusinessType: 'New Business Type',
      businessTypeRequired: 'Please select a business type',
      selectMerchant: 'Select merchant (optional)',
      proposedRentUnit: 'CNY',
      confirm: 'Confirm',
      cancel: 'Cancel',
      replaceTitle: 'Replace Brand',
      currentBrand: 'Current Brand',
      newBrand: 'New Brand',
      selectNewBrand: 'Select new brand',
      newMerchant: 'New Merchant (optional)',
      assignBrandRequired: 'Please select a brand',
      replaceBrandRequired: 'Please select a new brand',
      assignSuccess: 'Assigned successfully',
      assignFailed: 'Assignment failed',
      addBrand: 'Add Brand',
      adjustBrand: 'Adjust Brand',
      cancelPlacement: 'Cancel Placement',
      cancelPlacementConfirm: 'Confirm to cancel placement for shop "{shopName}"?',
      cancelPlacementTitle: 'Confirm Cancel Placement',
      cancelPlacementSuccess: 'Placement cancelled successfully',
      replaceSuccess: 'Replaced successfully',
      clearConfirmMsg: 'Confirm to clear placement info for shop "{shopName}"?',
      clearConfirmTitle: 'Confirm Clear',
      clearSuccess: 'Cleared successfully',
      listView: 'List View',
      canvasView: 'Canvas View',
      canvasPlaceholder: 'Canvas visualization component is under development, stay tuned...',
      backToList: 'Back to List View',
      selectFloorFirst: 'Please select a floor first',
      canvasTip: 'Click on a shop to perform placement operations',
      multiAssignTitle: 'Multi-Brand Placement',
      shopInfo: 'Shop Information',
      shopCode: 'Asset Code',
      shopType: 'Shop Type',
      shopTypeRetail: 'Retail',
      shopTypeFood: 'F&B',
      shopTypeEntertainment: 'Entertainment',
      shopTypeService: 'Service',
      filterIndustry: 'Industry',
      filterBrandLevel: 'Brand Level',
      filterBrandType: 'Brand Type',
      plannedBusinessType: 'Planned Business Type',
      placementBrands: 'Placement Brands',
      preferred: 'Preferred',
      secondary: 'Secondary',
      alternative: 'Alternative',
      preferredBrandRequired: 'Please select preferred brand',
      businessTypeRequired: 'Please select business type',
      existingBrands: 'Existing Brands',
      addMoreBrands: 'Add More Brands',
      statusLegend: 'Status Legend',
      shopDetail: 'Shop Details',
    },

    // ==================== Quick Intention ====================
    quickIntention: {
      title: 'Quick Create Intention',
      merchant: 'Merchant',
      selectMerchant: 'Select merchant',
      proposedRent: 'Proposed Rent',
      intentionDate: 'Intention Date',
      selectDate: 'Select date',
      remark: 'Remark',
      remarkPlaceholder: 'Enter remark',
      cancel: 'Cancel',
      confirm: 'Confirm',
      merchantRequired: 'Please select a merchant',
      proposedRentRequired: 'Please enter proposed rent',
      createSuccess: 'Intention created successfully',
      createFailed: 'Failed to create intention',
    },

    // ==================== Intention Deposit ====================
    deposit: {
      title: 'Intention Deposit',
      feeConfig: 'Fee Configuration',
      feeName: 'Fee Name',
      feeCode: 'Fee Code',
      feeItem: 'Fee Item',
      selectFeeItem: 'Select fee item',
      amount: 'Amount',
      remark: 'Remark',
      totalAmount: 'Total Amount',
      paymentRecords: 'Payment Records',
      receivePayment: 'Receive Payment',
      recordNo: 'Record No.',
      paymentMethod: 'Payment Method',
      paymentTime: 'Payment Time',
      payee: 'Payee',
      recordStatus: 'Status',
      recordDetail: 'Record Detail',
      // Payment methods
      cash: 'Cash',
      bankTransfer: 'Bank Transfer',
      alipay: 'Alipay',
      wechat: 'WeChat',
      check: 'Check',
      // Record status
      statusPending: 'Pending',
      statusReceived: 'Received',
      statusDeducted: 'Deducted',
      statusRefundPending: 'Refunding',
      statusRefunded: 'Refunded',
      // Statistics
      receivedAmount: 'Received',
      availableDeduct: 'Available for Deduction',
      // Actions
      deduct: 'Deduct',
      refund: 'Refund',
      refundApplied: 'Refund applied',
      // Dialog fields
      deductAmount: 'Deduct Amount',
      availableAmount: 'Available Amount',
      refundAmount: 'Refund Amount',
      refundableAmount: 'Refundable Amount',
      refundReason: 'Refund Reason',
      deductedAmount: 'Deducted Amount',
      effectiveTime: 'Effective Time',
    },

    // ==================== Formal Placement ====================
    formalPlacement: {
      title: 'Formal Placement',
      candidateIntentions: 'Candidate Intentions',
      pendingPlacements: 'Pending Placements',
      effectivePlacements: 'Effective Placements',
      cancelledPlacements: 'Cancelled',
      // Tabs
      candidatesTab: 'Candidates',
      placementsTab: 'Placements',
      // Fields
      intentionNo: 'Intention No.',
      brandName: 'Brand',
      merchantName: 'Merchant',
      shopName: 'Asset',
      proposedRent: 'Proposed Rent',
      depositStatus: 'Deposit',
      placementNo: 'Placement No.',
      startDate: 'Start Date',
      engineeringReqs: 'Engineering Requirements',
      statusLabel: 'Status',
      effectiveTime: 'Effective Time',
      cancelReason: 'Cancel Reason',
      // Status
      statusPending: 'Pending',
      statusEffective: 'Effective',
      statusCancelled: 'Cancelled',
      // Actions
      createPlacement: 'Create Placement',
      placementDetail: 'Placement Detail',
      effect: 'Effect',
      cancelPlacement: 'Cancel Placement',
      effectConfirm: 'Are you sure to effect this placement?',
      // Validation
      validateError: 'Validation failed, cannot create placement',
      // Search
      keyword: 'Keyword',
      noRecords: 'No placement records',
    },

    // ==================== Product Management ====================
    product: {
      title: 'Product Management',
      productCode: 'Product Code',
      productName: 'Product Name',
      merchantName: 'Merchant',
      contractNo: 'Contract No.',
      commissionRate: 'Rate(%)',
      productType: 'Type',
      isDefault: 'Default',
      status: 'Status',
      remark: 'Remark',
      addProduct: 'Add Product',
      editProduct: 'Edit Product',
      productDetail: 'Product Detail',
      productNameRequired: 'Product name is required',
      merchantRequired: 'Please select a merchant',
      contractRequired: 'Please select a contract',
      deleteConfirm: 'Are you sure to delete the selected products?',
      defaultCannotDelete: 'Default product cannot be deleted',
      salesLinkedCannotDelete: 'Products linked to sales records cannot be deleted',
      bindingCannotDelete: 'Product is bound to contracts, please unbind first',
      typeSingle: 'Single',
      typeCategory: 'Category',
      typeLease: 'Lease',
      typeJoint: 'Joint',
      typeSelf: 'Self-operated',
      yes: 'Yes',
      no: 'No',
      enable: 'Enabled',
      disable: 'Disabled',
      pendingGenerate: 'Auto-generated on activation',
      viewProductList: 'View Products',
      defaultProductInfo: 'Default Product Info',
      // Natural attributes
      category: 'Category',
      categoryPlaceholder: 'Select category',
      brand: 'Brand',
      brandPlaceholder: 'Select brand',
      barcode: 'Barcode',
      sku: 'SKU',
      specification: 'Specification',
      unit: 'Unit',
      imageUrl: 'Image',
      // Unit options
      unitPiece: 'Piece',
      unitKg: 'kg',
      unitSet: 'Set',
      unitCup: 'Cup',
      unitPortion: 'Portion',
      unitBox: 'Box',
      unitBottle: 'Bottle',
      // Entry operations
      addRow: 'Add Row',
      batchSave: 'Save All',
      batchDelete: 'Batch Delete',
      excelImport: 'Excel Import',
      downloadTemplate: 'Download Template',
      editMode: 'Edit Mode',
      saveRow: 'Save',
      cancelRow: 'Cancel',
      continueAdd: 'Add More Rows',
      // Binding
      binding: 'Binding',
      bindingCount: 'Bindings',
      bindProduct: 'Bind Product',
      unbind: 'Unbind',
      retailPrice: 'Retail Price',
      costPrice: 'Cost Price',
      purchasePrice: 'Purchase Price',
      wholesalePrice: 'Wholesale Price',
      memberPrice: 'Member Price',
      purchasePriceShort: 'Purchase',
      wholesalePriceShort: 'Wholesale',
      memberPriceShort: 'Member',
      retailPriceShort: 'Retail',
      settlementMethod: 'Business Mode',
      salesDeptName: 'Sales Dept',
      settlementRent: 'Rent',
      settlementCommission: 'Commission',
      settlementPurchase: 'Purchase',
      // Import
      importStep1: 'Download Template',
      importStep2: 'Upload File',
      importStep3: 'Validation',
      importStep4: 'Confirm Import',
      importTitle: 'Product Batch Import',
      importTemplateDesc: 'Download the template first and fill in data according to the format',
      importUploadTip: 'Supports .xlsx/.xls, max 1000 rows per import',
      importValidating: 'Validating data...',
      importTotalRows: 'Total Rows',
      importValidRows: 'Valid Rows',
      importInvalidRows: 'Invalid Rows',
      importSuccess: 'Import successful',
      importErrorDetail: 'Error Details',
      // Binding dialog
      bindingTitle: 'Contract Binding Management',
      bindingEmpty: 'No bound contracts',
      addBinding: 'Add Binding',
      selectContract: 'Select Contract',
      selectContractPlaceholder: 'Select a contract to bind',
      selectMerchant: 'Select Merchant',
      unbindConfirm: 'Are you sure to unbind this product from the contract?',
      unbindSuccess: 'Unbound successfully',
      bindSuccess: 'Bound successfully',
      updateBindingSuccess: 'Binding updated',
      isDefaultProduct: 'Default Product',
      sortOrder: 'Sort Order',
      // v4.3
      productStatus: 'Product Status',
      businessStatus: 'Business Status',
      businessStatusActive: 'Active',
      businessStatusPartial: 'Partial Expired',
      businessStatusExpired: 'All Expired',
      businessStatusUnbound: 'Unbound',
      contractStatus: 'Contract Status',
      contractStatusDraft: 'Draft',
      contractStatusPending: 'Pending',
      contractStatusActive: 'Active',
      contractStatusExpired: 'Expired',
      contractStatusTerminated: 'Terminated',
      assetName: 'Asset',
      contractEnd: 'Contract End',
      basicInfo: 'Basic Info',
      contractBinding: 'Contract Binding',
      removeBindingRow: 'Remove',
      removeBindingConfirm: 'Sure to remove this binding row?',
      imagePlaceholder: 'Click to upload product image',
      importMergeHint: 'Same-name products will be auto-merged with separate contract bindings',
      // New basic info fields
      fullName: 'Full Name',
      fullNamePlaceholder: 'Product full name',
      pinyinCode: 'Pinyin Code',
      pinyinCodePlaceholder: 'Auto-generated from product name',
      barcodeType: 'Barcode Type',
      barcodeTypeEan13: 'EAN-13',
      barcodeTypeCode128: 'CODE-128',
      barcodeTypeUpcA: 'UPC-A',
      barcodeTypeCustom: 'Custom',
      scanBarcode: 'Scan Barcode',
      // Physical attributes
      origin: 'Origin',
      originPlaceholder: 'Place of production',
      season: 'Season',
      seasonSpring: 'Spring',
      seasonSummer: 'Summer',
      seasonAutumn: 'Autumn',
      seasonWinter: 'Winter',
      seasonAll: 'All Seasons',
      seasonNone: 'None',
      shelfLifeDays: 'Shelf Life (days)',
      weight: 'Net Weight (kg)',
      colorPattern: 'Color/Pattern',
      colorPatternPlaceholder: 'Color/pattern',
      qualityGrade: 'Quality Grade',
      qualityGradeQualified: 'Standard',
      qualityGradeFirst: 'First Grade',
      qualityGradeTop: 'Premium',
      textureContent: 'Texture Content',
      textureContentPlaceholder: 'e.g. Cotton 80% Polyester 20%',
      // Tax info
      taxInfo: 'Tax Information',
      customsTaxRate: 'Customs Tax Rate (%)',
      consumptionTaxRate: 'Consumption Tax Rate (%)',
      inputTaxRate: 'Input Tax Rate (%)',
      outputTaxRate: 'Output Tax Rate (%)',
      taxCategory: 'Tax Classification Code',
      taxCategoryPlaceholder: 'Tax classification code',
      collapseTaxInfo: 'Click arrow to expand/collapse',
      // Flags
      flags: 'Flags',
      isBulk: 'Bulk',
      isSpecialSale: 'Special Sale',
      allowDecimalQty: 'Allow Decimal Qty',
      isFresh: 'Fresh',
      // SKU management
      skuManage: 'SKU Management',
      skuConfig: 'SKU Configuration',
      dimName: 'Dimension Name',
      dimNamePlaceholder: 'e.g. Color / Flavor',
      dim2NamePlaceholder: 'Optional, e.g. Size / Capacity',
      dimValues: 'Dimension Values',
      dimValuesPlaceholder: 'Press Enter to add',
      skuPreview: 'SKU Preview',
      skuPreviewCount: 'SKUs',
      skuBasedOnConfig: '(based on current dimension config)',
      confirmSplit: 'Confirm Split',
      manualAddSku: 'Add SKU Manually',
      skuCode: 'SKU Code',
      skuName: 'SKU Name',
      skuBarcode: 'SKU Barcode',
      pluCode: 'PLU Code',
      dim1Name: 'Dimension 1',
      dim1Value: 'Dim 1 Value',
      dim2Name: 'Dimension 2',
      dim2Value: 'Dim 2 Value',
      isSalesReport: 'Sales Report',
      isInventoryManage: 'Inventory',
      skuEmpty: 'No SKUs generated. Configure dimensions above and click "Confirm Split"',
      skuListInfo: 'SKU List',
      skuImport: 'Import SKUs',
      skuExportEmpty: 'Export Empty',
      skuCount: 'SKU Count',
      skuCountUnit: '',
      bindingNote: 'Note',
      bindingNoteContent: 'Retail/purchase/wholesale/member prices and commission rate are determined per contract. Single-type products use the retail price set in the bound contract; Category-type products have cashiers manually enter the actual price at POS.',
      // Form mode
      backToList: 'Back to List',
      saveSuccess: 'Saved successfully',
      addMode: 'Add Mode',
      // Tabs
      tabBasic: 'Basic Info',
      tabBinding: 'Contract Binding',
      tabSku: 'SKU Management',
      // Import
      importStep1: 'Download Template',
      importStep2: 'Upload File',
      importStep3: 'Validation',
      importStep4: 'Confirm Import',
      importTitle: 'Product Batch Import',
      importTemplateDesc: 'Download the template first and fill in data',
      importUploadTip: 'Supports .xlsx/.xls, max 1000 rows',
      importValidating: 'Validating...',
      importTotalRows: 'Total Rows',
      importValidRows: 'Valid Rows',
      importInvalidRows: 'Invalid Rows',
      importSuccess: 'Import successful',
      importErrorDetail: 'Error Details',
      // Binding dialog
      bindingTitle: 'Contract Binding Management',
      bindingEmpty: 'No bound contracts',
      addBinding: 'Add Binding',
      selectContract: 'Select Contract',
      selectContractPlaceholder: 'Select a contract to bind',
      selectMerchant: 'Select Merchant',
      unbindConfirm: 'Sure to unbind this product from the contract?',
      unbindSuccess: 'Unbound successfully',
      bindSuccess: 'Bound successfully',
      updateBindingSuccess: 'Binding updated',
      isDefaultProduct: 'Default Product',
      sortOrder: 'Sort Order',
      // v4.3 new
      productStatus: 'Product Status',
      businessStatus: 'Business Status',
      businessStatusActive: 'Active Contract',
      businessStatusPartial: 'Partial Expired',
      businessStatusExpired: 'All Expired',
      businessStatusUnbound: 'Unbound',
      contractStatus: 'Contract Status',
      contractStatusDraft: 'Draft',
      contractStatusPending: 'Pending',
      contractStatusActive: 'Active',
      contractStatusExpired: 'Expired',
      contractStatusTerminated: 'Terminated',
      assetName: 'Asset',
      contractEnd: 'Contract End Date',
      basicInfo: 'Basic Info',
      contractBinding: 'Contract Binding',
      removeBindingRow: 'Remove',
      removeBindingConfirm: 'Sure to remove this binding row?',
      // v4.4 prototype alignment
      pageTitle: 'Product Management',
      totalCount: '{count} products in total',
      keywordSearch: 'Keyword',
      keywordPlaceholder: 'Product name / Code / Barcode / SKU',
      codeAutoGenerate: 'Auto generated, editable',
      codeManualInput: 'Please enter product code',
      productCodeRequired: 'Please enter product code',
      productNamePlaceholder: 'Enter product name',
      fullNamePlaceholder: 'Full product name',
      pinyinCodePlaceholder: 'Auto generated from name',
      brandPlaceholder: 'Select brand',
      clickUpload: 'Click to upload\nproduct image',
      imageHint: 'JPG/PNG\n800×800 recommended\nMax 5MB',
      productImage: 'Product Image',
      identityAndSpec: 'Identity & Specification',
      physicalAttr: 'Physical Attributes',
      materialContent: 'Material Content',
      materialContentPlaceholder: 'e.g. Cotton 80% Polyester 20%',
      clickToToggle: '(Click arrow to collapse/expand)',
      daysPlaceholder: 'Days',
      kgPlaceholder: 'kg',
      originPlaceholder: 'Place of origin',
      colorPatternPlaceholder: 'Color/Pattern',
      skuEmptyTip: 'No SKUs yet. Configure dimensions above and click "Confirm Split"',
      addManualSku: 'Add SKU Manually',
      dim1NameLabel: 'Dimension 1 name (e.g. Color / Flavor)',
      dim2NameLabel: 'Dimension 2 name (e.g. Size / Capacity) [Optional]',
      dim1ValuesLabel: 'Dimension 1 values',
      dim2ValuesLabel: 'Dimension 2 values',
      tagInputPlaceholder: 'Type and press Enter to add',
      skuList: 'SKU List',
      isDefault: 'Default',
      sort: 'Sort',
      importSku: 'Import SKU',
      boundCount: '{count} contracts bound',
      unbind: 'Unbind',
      wholesalePrice: 'Wholesale (¥)',
      memberPrice: 'Member (¥)',
      bindingNoteTitle: 'Note',
      bindingNoteContent: 'Product retail/purchase/wholesale/member prices and commission rate are determined per contract. Single-type products use the retail price set in the bound contract; Category-type products have cashiers manually enter the actual price at POS.',
      statusActive: 'Active',
      statusEditing: 'Editing',
      statusDraft: 'Draft',
      viewMode: 'View Mode',
      exportData: 'Export',
      exportTip: 'Export feature coming soon',
      autoGenerate: '(Auto generate)',
      // Approval workflow
      approvalStatus: 'Approval Status',
      approvalDraft: 'Draft',
      approvalApproved: 'Approved',
      approve: 'Approve',
      approveConfirm: 'Confirm to approve this product? Core data and prices cannot be modified after approval.',
      approveSuccess: 'Approved successfully',
      skuRequired: 'Product must have at least one SKU record, please add SKU first',
      copyProduct: 'Copy',
      copySuccess: 'Product info copied. Edit and save to create a new product',
    },

    // ==================== Contract Termination ====================
    termination: {
      // Titles
      createTitle: 'Create Termination Request',
      editTitle: 'Edit Termination Request',
      detailTitle: 'Termination Detail',
      tabLabel: 'Termination',
      tabTitle: 'Termination',
      initTermination: 'Initiate Exit',
      // Termination type
      type: 'Termination Type',
      terminationType: 'Termination Type',
      normalExpiry: 'Normal Expiry',
      earlyTermination: 'Early Termination',
      // Basic info
      exitDate: 'Exit Date',
      exitDatePlaceholder: 'Select exit date',
      exitDateRequired: 'Exit date is required',
      contractEnd: 'Contract End Date',
      exitReason: 'Exit Reason',
      exitReasonPlaceholder: 'Select exit reason',
      reasonExpired: 'Contract Expired',
      reasonBreach: 'Breach',
      reasonNegotiated: 'Negotiated',
      reasonOther: 'Other',
      contractNo: 'Contract No.',
      merchantName: 'Merchant Name',
      assetName: 'Asset Name',
      remark: 'Remark',
      remarkPlaceholder: 'Enter remark',
      createAndCalc: 'Create & Calculate',
      // Settlement
      penaltyAmount: 'Penalty Amount',
      penaltyAmountPlaceholder: 'Enter penalty amount',
      totalOutstanding: 'Total Outstanding',
      totalRefundable: 'Total Refundable',
      depositAmount: 'Deposit Amount',
      netSettlement: 'Net Settlement',
      settlementRemark: 'Settlement Remark',
      viewSettlement: 'View Settlement',
      unpaidPlans: 'Unpaid Bills',
      refundableFees: 'Refundable Fees',
      // Status
      statusDraft: 'Draft',
      statusPending: 'Pending Approval',
      statusCompleted: 'Completed',
      statusCancelled: 'Cancelled',
      statusTerminating: 'Terminating',
      // Approval
      approvalStatus: 'Approval Status',
      submitApproval: 'Submit for Approval',
      submitConfirmTitle: 'Submit Termination',
      submitConfirmMsg: 'After submission, it will enter the approval process (Leasing → Finance → Operations). Confirm?',
      submitSuccess: 'Submitted for approval',
      submitFailed: 'Submit failed',
      // Actions
      noRecord: 'No termination records',
      createBy: 'Created By',
      createTime: 'Created At',
      deleteConfirmTitle: 'Confirm Delete',
      deleteConfirmMsg: 'Sure to delete this termination request?',
      deleteSuccess: 'Deleted',
      deleteFailed: 'Delete failed',
      createSuccess: 'Termination request created',
      createFailed: 'Failed to create termination request',
      loadFailed: 'Failed to load termination records',
      settlementLoadFailed: 'Failed to load settlement info',
      // Termination management page
      pageTitle: 'Termination Management',
      keywordSearch: 'Keyword',
      keywordPlaceholder: 'Termination No./Contract/Merchant/Shop',
      typeFilter: 'Termination Type',
      statDraft: 'Draft',
      statPending: 'Pending',
      statCompleted: 'Completed',
      statCancelled: 'Cancelled',
      colTerminationNo: 'Termination No.',
      colContractNo: 'Contract No.',
      colMerchant: 'Merchant',
      colAsset: 'Asset',
      colType: 'Type',
      colExitDate: 'Exit Date',
      colNetSettlement: 'Net Settlement',
      colStatus: 'Status',
      goContract: 'View Contract',
      processProgress: 'Process Progress',
      nodeRecruitment: 'Leasing Confirm',
      nodeFinance: 'Finance Settlement',
      nodeOperation: 'Operations Handover',
      statisticsFailed: 'Failed to load statistics',
    },

    // ==================== Merchant Account ====================
    account: {
      title: 'Merchant Account',
      subtitle: 'Manage merchant system accounts',
      merchantName: 'Merchant Name',
      accountCount: 'Account Count',
      addAccount: 'Add Account',
      unbind: 'Unbind',
      unbindConfirm: 'Confirm unbind this account? Only the merchant association will be cleared, the account itself will be kept.',
      unbindWx: 'Unbind WeChat',
      unbindWxConfirm: 'Unbind WeChat Mini Program for this account? They must re-bind to log in with WeChat.',
      unbindWxSuccess: 'WeChat unbound successfully',
      resetPassword: 'Reset Password',
      resetPasswordConfirm: 'Confirm reset the password for this account?',
      addSuccess: 'Account created successfully',
      generatedPassword: 'Generated Password',
      copyPassword: 'Copy Password',
      copiedSuccess: 'Password copied to clipboard',
      phone: 'Phone',
      realname: 'Name',
      lastLogin: 'Last Login',
      noAccounts: 'No linked accounts',
      phonePlaceholder: 'Enter phone number',
      realnamePlaceholder: 'Enter name',
      remarkPlaceholder: 'Enter remark (optional)',
      username: 'Username',
      passwordDialogTitle: 'Account Password',
      passwordTip: 'Please save the password below. It will not be shown again after closing.',
      enableSuccess: 'Enabled successfully',
      disableSuccess: 'Disabled successfully',
      unbindSuccess: 'Unbound successfully',
      resetSuccess: 'Password reset successfully',
      keywordPlaceholder: 'Merchant name/code/phone',
      userType: 'Account Type',
      staffAndMerchant: 'Staff+Merchant',
      merchantOnly: 'Merchant',
      wxBound: 'WeChat',
      wxBoundYes: 'Bound',
      wxBoundNo: 'Unbound',
      wxNickname: 'WeChat Nickname',
      wxAvatar: 'WeChat Avatar',
      wxAccount: 'WeChat Account',
      reusedSuccess: 'Existing account linked to merchant. Use original password to login.',
      bindExisting: 'Bind Account',
      searchUser: 'Select User',
      searchUserPlaceholder: 'Search by username/name/phone',
      searchMinLength: 'Please enter at least 2 characters',
      noSearchResult: 'No bindable users found',
      confirmBind: 'Confirm Bind',
      bindSuccess: 'Account bound successfully',
      bindStores: 'Stores',
      bindStoresPlaceholder: 'Select store(s)',
      bindStoresRequired: 'Select at least one store',
      storeOptionsEmpty: 'No stores available. Please create stores in Organization first.',
      storeColumn: 'Stores',
      editStores: 'Configure Stores',
      editStoresTitle: 'Configure Operable Stores',
      editStoresSuccess: 'Store configuration updated',
      defaultStore: 'Default Store',
    },

    // ==================== Invoice Management ====================
    invoice: {
      title: 'Invoice Management',
      management: 'Invoice Management',
      // Invoice Request (Merchant)
      requestTitle: 'Invoice Request',
      myRequests: 'My Requests',
      createRequest: 'Request Invoice',
      selectBills: 'Select Bills',
      selectBillsPlaceholder: 'Click to select bills for invoicing',
      billsUnit: 'bills',
      addMore: 'Add More',
      billSelection: 'Bill Selection',
      deliverType: 'Delivery Method',
      deliverTypeWechat: 'WeChat Card Package',
      deliverTypeEmail: 'Email Delivery',
      deliverTypeEmailHint: 'Invoice will be sent to the following email',
      buyerEmail: 'Billing Email',
      buyerEmailPlaceholder: 'Enter billing email',
      remark: 'Remark',
      remarkPlaceholder: 'Enter remark (optional)',
      requestNo: 'Request No.',
      requestTime: 'Request Time',
      totalAmount: 'Invoice Amount',
      titleType: 'Title Type',
      titleTypePersonal: 'Personal',
      titleTypeCompany: 'Company',
      buyerName: 'Invoice Title',
      buyerTaxNo: 'Tax ID',
      buyerPhone: 'Buyer Phone',
      status: 'Status',
      invoiceStatus: 'Invoice Status',
      // Bill Invoice Status
      invoiceStatusNone: 'Available',
      invoiceStatusApplied: 'Applied',
      invoiceStatusInvoicing: 'Invoicing',
      invoiceStatusInvoiced: 'Invoiced',
      invoiceStatusFailed: 'Failed',
      invoiceStatusRedFlushed: 'Red Flushed',
      applicant: 'Applicant',
      // Request Status
      statusPending: 'Pending',
      statusProcessing: 'Processing',
      statusDone: 'Completed',
      statusFailed: 'Failed',
      // Invoice Processing (Finance)
      processTitle: 'Invoice Processing',
      pendingRequests: 'Pending Requests',
      issueInvoice: 'Issue Invoice',
      invoiceType: 'Invoice Type',
      invoiceTypeSpecial: 'VAT Special Invoice',
      invoiceTypeOrdinary: 'VAT Ordinary Invoice',
      invoiceTypePending: 'Pending',
      confirmIssue: 'Confirm Issue',
      confirmIssueTip: 'Confirm to issue this invoice? Cannot be undone after issuance.',
      issueSuccess: 'Invoice request submitted, please check status later',
      issueFailed: 'Invoice issuance failed',
      // Invoice Record
      recordTitle: 'Invoice Records',
      recordList: 'Invoice List',
      invoiceNo: 'Invoice No.',
      invoiceDate: 'Invoice Date',
      invoiceAmount: 'Invoice Amount',
      elecInvoiceNumber: 'E-Invoice No.',
      invoiceCode: 'Invoice Code',
      invoiceSerialNo: 'Invoice Serial No.',
      exTaxAmount: 'Ex-Tax Amount',
      pdfUrl: 'Invoice PDF',
      downloadPdf: 'Download PDF',
      redFlush: 'Red Flush',
      redInvoice: 'Red Invoice',
      redFlushConfirm: 'Confirm to red flush this invoice?',
      redFlushReason: 'Red Flush Reason',
      redFlushReasonPlaceholder: 'Enter red flush reason',
      redFlushSuccess: 'Red flushed successfully',
      redFlushFailed: 'Red flush failed',
      deliver: 'Re-deliver',
      deliverConfirm: 'Confirm to re-deliver this invoice?',
      deliverSuccess: 'Delivered successfully',
      deliverFailed: 'Delivery failed',
      viewDetail: 'View Details',
      // Details
      detailTitle: 'Invoice Details',
      invoiceInfo: 'Invoice Record',
      buyerInfo: 'Buyer Info',
      platform: 'Invoice Platform',
      viewPdf: 'View PDF',
      platformBillNo: 'Platform Serial No.',
      invoiceTypeLabel: 'Invoice Type',
      invoiceItems: 'Invoice Items',
      goodsName: 'Goods Name',
      taxClassifyCode: 'Tax Classification Code',
      unit: 'Unit',
      quantity: 'Quantity',
      unitPrice: 'Unit Price',
      amount: 'Amount',
      taxRate: 'Tax Rate',
      taxAmount: 'Tax Amount',
      // Messages
      selected: 'Selected',
      noPaidBills: 'No invoiceable bills available',
      selectAtLeastOne: 'Please select at least one bill',
      emailRequired: 'Please enter billing email',
      invalidEmail: 'Please enter a valid email address',
      requestSuccess: 'Invoice request submitted',
      requestFailed: 'Request submission failed',
      // Bill Selection
      billableAmount: 'Invoiceable Amount',
      billFeeDetail: 'Bill Fee Details',
      defaultGoodsName: 'Real Estate Leasing Service',
      noFeeItems: 'No fee items',
      invoiceGoodsName: 'Invoice Goods Name',
      // Summary Data
      pendingAmount: 'Pending Amount',
      todayIssued: 'Issued Today',
      monthlyTotal: 'Monthly Total',
      totalCount: 'Total Invoices',
      specialCount: 'VAT Special',
      ordinaryCount: 'VAT Ordinary',
      // Related bills
      relatedBills: 'Related Bills',
      billNo: 'Bill No.',
      // Red flush info
      redFlushInfo: 'Red Flush Info',
      redFlushTime: 'Red Flush Time',
      redFlushBy: 'Red Flush Operator',
      // Merchant Invoice Info
      merchantInvoiceInfo: 'Merchant Invoice Info',
      isDefault: 'Default',
      titleName: 'Invoice Title',
      taxNo: 'Tax ID',
      phone: 'Phone',
      email: 'Email',
      bankName: 'Bank Name',
      bankAccount: 'Bank Account',
      address: 'Registered Address',
      // Delivery Info
      deliverInfo: 'Delivery Info',
      buyerEmail: 'Buyer Email',
      // Invoice Info Selection
      selectInvoiceInfo: 'Select Invoice Info',
      selectInvoiceInfoPlaceholder: 'Please select invoice info',
      selectInvoiceInfoRequired: 'Please select invoice info',
      manageInvoiceInfo: 'Manage Invoice Info',
      goToManageInvoiceInfo: 'Go to manage invoice info',
      noInvoiceInfo: 'No invoice info for this merchant',
      setDefaultSuccess: 'Set as default successfully',
      setDefaultFailed: 'Failed to set as default',
    },

    // ==================== Merchant Journey ====================
    journey: {
      title: 'Merchant Journey',
      searchPlaceholder: 'Search merchant name or brand',
      noMerchant: 'Select a merchant to view journey',
      merchantProfile: 'Merchant Profile',
      firstContact: 'First Contact',
      relatedShops: 'Related Shops',
      contractCount: 'Contracts',
      currentStatusLabel: 'Current Status',
      timelineTitle: 'Lifecycle Timeline',
      stageFirstContact: 'First Contact',
      stageIntention: 'Intention',
      stageContractSigned: 'Contract Signed',
      stageRenovation: 'Renovation',
      stageOperation: 'Operation',
      stageExit: 'Exit',
      statusCompleted: 'Completed',
      statusInProgress: 'In Progress',
      statusUpcoming: 'Upcoming',
      statusRecruiting: 'Recruiting',
      statusNegotiating: 'Negotiating',
      statusIntention: 'Intention',
      statusContracting: 'Contracting',
      statusOperating: 'Operating',
      statusExited: 'Exited',
      stageDetail: 'Stage Detail',
      negotiationRounds: 'Negotiation Rounds',
      intentionShop: 'Intended Shop',
      intentionStatus: 'Intention Status',
      contractNo: 'Contract No.',
      leaseShop: 'Leased Shop',
      leasePeriod: 'Lease Period',
      rentMode: 'Rent Mode',
      approvalStatus: 'Approval Status',
      renovationType: 'Renovation Type',
      renovationPeriod: 'Renovation Period',
      renovationProgress: 'Renovation Progress',
      monthlySales: 'Monthly Sales Trend',
      totalSales: 'Total Sales',
      avgMonthlySales: 'Avg Monthly Sales',
      paymentCount: 'Payment Count',
      exitReason: 'Exit Reason',
      exitDate: 'Exit Date',
      viewContract: 'View Contract',
      viewShop: 'View Shop',
      noStages: 'No journey data',
      // ==================== Overview ====================
      overview: {
        backToList: 'Back to Overview',
        kpiActive: 'Active Merchants',
        kpiConversion: 'Intention→Sign Rate',
        kpiNewThisMonth: 'New This Month',
        kpiWarnings: 'Warnings',
        funnelTitle: 'Stage Funnel',
        statusTitle: 'Status Distribution',
        newTrendTitle: 'New Merchants 6M',
        salesTrendTitle: 'Total Sales 6M',
        matrixTitle: 'Merchant Journey Matrix',
        filterAll: 'All',
        filterWarning: 'Warnings Only',
        colMerchant: 'Merchant',
        colBrand: 'Brand',
        colStage: 'Stage Progress',
        colStatus: 'Status',
        colContracts: 'Contracts',
        colWarning: 'Warning',
        colAction: 'Action',
        stageFirstContact: 'Contact',
        stageIntention: 'Intention',
        stageContractSigned: 'Signed',
        stageRenovation: 'Renovation',
        stageExit: 'Exited',
        warnRenovationOverdue: 'Renovation overdue',
        warnIntentionStalled: 'Intention stalled >30d',
        warnContractExpiring: 'Contract expires <60d',
        warnShortRenovationOverdue: 'Reno',
        warnShortIntentionStalled: 'Stall',
        warnShortContractExpiring: 'Soon',
        statusMap: {
          OPERATING: 'Operating',
          CONTRACTING: 'Contracting',
          NEGOTIATING: 'Negotiating',
          RECRUITING: 'Recruiting',
          EXITED: 'Exited',
        },
        detail: 'Detail',
        loadFailed: 'Failed to load overview',
        retry: 'Retry',
        empty: 'No journey data',
      },
    },

    // ==================== Common ====================
    common: {
      hideSearch: 'Hide Search',
      showSearch: 'Show Search',
      warning: 'Warning',
      selectDataFirst: 'Please select data to delete',
      addSuccess: 'Added successfully',
      editSuccess: 'Updated successfully',
      setSuccess: 'Set successfully',
      cancelSuccess: 'Cancelled successfully',
      pleaseInput: 'Please enter',
      pleaseSelect: 'Please select',
    },
  },
}
